import { createClient } from "@supabase/supabase-js";
import CONFIG from "./config";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const IS_DEMO_PAGE = typeof window !== "undefined" && window.location.pathname === "/demo";

const rawSupabase = createClient(SUPABASE_URL, SUPABASE_KEY);

const WRITE_METHODS = ["insert", "update", "upsert", "delete"];

function demoBlockedChain() {
  const err = { message: "This is a demo — changes aren't saved here. Create an account to use this feature." };
  const chain = new Proxy(() => {}, {
    get(_, prop) {
      if (prop === "then") return (resolve) => resolve({ data: null, error: err });
      if (prop === "catch") return () => chain;
      if (prop === "finally") return (fn) => { fn?.(); return chain; };
      return () => chain;
    },
    apply() { return chain; },
  });
  return chain;
}

function guardTable(realTable) {
  return new Proxy(realTable, {
    get(target, prop, receiver) {
      if (WRITE_METHODS.includes(prop)) {
        return () => demoBlockedChain();
      }
      const value = Reflect.get(target, prop, receiver);
      return typeof value === "function" ? value.bind(target) : value;
    },
  });
}

export const supabase = IS_DEMO_PAGE
  ? new Proxy(rawSupabase, {
      get(target, prop, receiver) {
        if (prop === "from") {
          return (table) => guardTable(rawSupabase.from(table));
        }
        const value = Reflect.get(target, prop, receiver);
        return typeof value === "function" ? value.bind(target) : value;
      },
    })
  : rawSupabase;


// ── AUTH ──────────────────────────────────────────────────────────────────────
export async function signInWithGoogle(emailHint) {
  const redirectTo = window.location.origin;
  const { error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo,
      queryParams: {
        access_type: "offline",
        prompt: "consent",
        ...(emailHint ? { login_hint: emailHint } : {}),
      },
    },
  });
  if (error) throw error;
}

export async function signOut() {
  await supabase.auth.signOut();
}

export async function getSession() {
  const { data: { session } } = await supabase.auth.getSession();
  return session;
}

export function onAuthStateChange(callback) {
  return supabase.auth.onAuthStateChange((event, session) => {
    callback(event, session);
  });
}

// ── VENDOR ────────────────────────────────────────────────────────────────────
const VENDOR_ALIASES = [
  "sharonloganparker@gmail.com",
];
const PRIMARY_VENDOR_EMAIL = "brentparker76@gmail.com";

export function isVendorAlias(email) {
  return VENDOR_ALIASES.includes(email);
}

export async function seedSampleData(vendorId) {
  const { error } = await supabase.rpc("seed_sample_data", { p_vendor_id: vendorId });
  if (error) throw error;
}

export async function markOnboardingTourSeen(vendorId) {
  const { error } = await supabase
    .from("vendors")
    .update({ onboarding_tour_seen: true })
    .eq("id", vendorId);
  if (error) throw error;
}

export async function fetchAllMarketIntelReports(vendorId, columns = "*") {
  const rows = [];
  let from = 0;
  for (let page = 0; page < 100; page++) {
    const { data, error } = await supabase
      .from("tcgplayer_reports")
      .select(columns)
      .eq("vendor_id", vendorId)
      .order("report_month", { ascending: false })
      .order("report_tier", { ascending: true })
      .order("rank", { ascending: true })
      .order("product_name", { ascending: true })
      .range(from, from + 999);
    if (error) throw error;
    if (!data || data.length === 0) break;
    rows.push(...data);
    from += data.length;
  }
  return rows;
}

export async function getVendor() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const emailToQuery = VENDOR_ALIASES.includes(user.email)
    ? PRIMARY_VENDOR_EMAIL
    : user.email;

  let { data } = await supabase
    .from("vendors")
    .select("*")
    .eq("email", emailToQuery)
    .maybeSingle();

  if (!data) {
    const { data: teamRow } = await supabase
      .from("vendor_users")
      .select("vendor_id")
      .eq("email", emailToQuery.toLowerCase())
      .maybeSingle();

    if (teamRow?.vendor_id) {
      const result = await supabase
        .from("vendors")
        .select("*")
        .eq("id", teamRow.vendor_id)
        .single();
      data = result.data;
    }
  }

  if (!data) throw new Error("Vendor not found for " + user.email);

  if (data.status === "suspended") {
    const suspendedErr = new Error("This account has been deactivated.");
    suspendedErr.code = "ACCOUNT_SUSPENDED";
    throw suspendedErr;
  }

  return data;
}

export async function createVendorAccount({ firstName, lastName, businessName, state, termsAccepted, referredBy, trialCode }) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch("/create-vendor", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
    },
    body: JSON.stringify({ firstName, lastName, businessName, state, termsAccepted, referredBy, trialCode }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || "Failed to create account");
  return body.vendor;
}

let _vendorId = null;
async function getVendorId() {
  if (_vendorId) return _vendorId;
  const vendor = await getVendor();
  _vendorId = vendor?.id;
  return _vendorId;
}
export function clearVendorCache() { _vendorId = null; }

export async function uploadVendorLogo(file, vendorId) {
  const ext  = file.name.split(".").pop().toLowerCase() || "png";
  const path = `${vendorId}/logo.${ext}`;

  const { error: uploadErr } = await supabase.storage
    .from("vendor-assets")
    .upload(path, file, { upsert: true, contentType: file.type });

  if (uploadErr) throw new Error("Logo upload failed: " + uploadErr.message);

  const { data: { publicUrl } } = supabase.storage
    .from("vendor-assets")
    .getPublicUrl(path);

  const { error: updateErr } = await supabase
    .from("vendors")
    .update({ logo_url: publicUrl })
    .eq("id", vendorId);

  if (updateErr) throw new Error("Failed to save logo URL: " + updateErr.message);
  return publicUrl;
}

export async function uploadCardImage(vendorId, blob) {
  const MAX_BYTES = 5 * 1024 * 1024;
  if (blob.size > MAX_BYTES) {
    throw new Error(`Image is too large (${(blob.size/1024/1024).toFixed(1)}MB). Max size is 5MB.`);
  }

  const path = `${vendorId}/${Date.now()}-${Math.random().toString(36).slice(2,8)}.jpg`;

  const { error: uploadErr } = await supabase.storage
    .from("card-images")
    .upload(path, blob, { upsert: false, contentType: "image/jpeg" });

  if (uploadErr) throw new Error("Image upload failed: " + uploadErr.message);

  const { data: { publicUrl } } = supabase.storage
    .from("card-images")
    .getPublicUrl(path);

  return publicUrl;
}

export async function finishLotAllocation(lotId, totalPaid, markupRate = 1.25) {
  const { data: items, error: fetchErr } = await supabase
    .from("inventory")
    .select("id, market_value_at_acquisition")
    .eq("lot_id", lotId)
    .neq("status", "Deleted");
  if (fetchErr) throw fetchErr;
  if (!items?.length) throw new Error("No cards found for this deal");

  const totalMarketValue = items.reduce((s,i) => s + (parseFloat(i.market_value_at_acquisition)||0), 0);
  const ratio = totalMarketValue > 0 ? +(parseFloat(totalPaid) / totalMarketValue).toFixed(6) : 0;

  for (const item of items) {
    const mv = parseFloat(item.market_value_at_acquisition) || 0;
    const ac = mv > 0 ? +(mv * ratio).toFixed(2) : 0;
    const ms = ac > 0 ? Math.max(1, Math.ceil(ac * markupRate)) : null;
    const { error: updErr } = await supabase.from("inventory")
      .update({ acquisition_cost: ac, min_sell_price: ms })
      .eq("id", item.id);
    if (updErr) throw updErr;
  }

  const { error: lotErr } = await supabase.from("lots").update({
    total_market_value: totalMarketValue,
    allocation_ratio: ratio,
    card_count: items.length,
  }).eq("id", lotId);
  if (lotErr) throw lotErr;

  return { totalMarketValue, ratio, cardCount: items.length };
}

export async function updateVendorProfile(vendorId, updates) {
  const allowed = {};
  if (updates.business_name !== undefined) allowed.business_name = updates.business_name;
  if (updates.logo_url      !== undefined) allowed.logo_url      = updates.logo_url;
  if (updates.trade_credit_rate !== undefined) allowed.trade_credit_rate = updates.trade_credit_rate;
  if (updates.cash_buy_rate     !== undefined) allowed.cash_buy_rate     = updates.cash_buy_rate;
  if (updates.markup_rate       !== undefined) allowed.markup_rate       = updates.markup_rate;
  if (updates.breakeven_buffer  !== undefined) allowed.breakeven_buffer  = updates.breakeven_buffer;
  const { data, error } = await supabase
    .from("vendors").update(allowed).eq("id", vendorId).select().single();
  if (error) throw error;
  return data;
}


function toApp(row) {
  if (!row) return null;
  return {
    ID:                        row.id,
    Type:                      row.type,
    CardName:                  row.card_name,
    Set:                       row.set_name,
    Number:                    row.card_number,
    Rarity:                    row.rarity,
    Language:                  row.language || "English",
    Condition:                 row.condition,
    Variant:                   row.variant || "",
    ImageURLs:                 row.image_urls || [],
    CustomCategory:            row.custom_category || "",
    ItemType:                  row.item_type || "",
    Description:               row.description || "",
    Tags:                      row.tags || [],
    ProductType:               row.product_type || "",
    PaymentMethod:             row.payment_method || "",
    Grade:                     row.grade,
    Quantity:                  row.quantity,
    Source:                    row.source,
    AcquisitionCost:           row.acquisition_cost != null ? parseFloat(row.acquisition_cost) : "",
    ListPrice:                 row.list_price != null ? parseFloat(row.list_price) : "",
    OnlinePrice:               row.online_price != null ? parseFloat(row.online_price) : null,
    MinSellPrice:              row.min_sell_price ? parseFloat(row.min_sell_price) : "",
    TaxRate:                   row.tax_rate != null ? parseFloat(row.tax_rate) : null,
    TaxAmount:                 row.tax_amount != null ? parseFloat(row.tax_amount) : null,
    MarketValueAtAcquisition:  row.market_value_at_acquisition ? parseFloat(row.market_value_at_acquisition) : "",
    AcquisitionDate:           row.acquisition_date,
    LotID:                     row.lot_id || "",
    LotName:                   row.lot_name || "",
    LotItemNumber:             row.lot_item_number || "",
    ShowID:                    row.show_id || "",
    ShowName:                  row.show_name || "",
    AcquiredShowID:            row.acquired_show_id || "",
    Location:                  row.location || "",
    ImageURL:                  row.image_url || "",
    QRCode:                    row.qr_code || "",
    Status:                    row.status,
    SoldPrice:                 row.sold_price ? parseFloat(row.sold_price) : "",
    SoldDate:                  row.sold_date || "",
    SoldAt:                    row.sold_at || null,
    SoldChannel:               row.sold_channel || "",
    BuyerGender:               row.buyer_gender || "",
    TradeCredit:               row.trade_credit ? parseFloat(row.trade_credit) : "",
    TradeGain:                 row.trade_gain   ? parseFloat(row.trade_gain)   : "",
    GradingCompany:            row.grading_company    || "",
    GradingSentDate:           row.grading_sent_date  || "",
    GradingReturnDate:         row.grading_return_date|| "",
    GradingEstimatedValue:     row.grading_estimated_value ? parseFloat(row.grading_estimated_value) : "",
    GradingCost:               row.grading_cost        ? parseFloat(row.grading_cost)        : "",
    Notes:                     row.notes || "",
    IsConsignment:             row.is_consignment || false,
    IsOnline:                  row.is_online      || false,
    PriceOverride:             row.price_override || false,
    ConsignPayoutStatus:       row.consign_payout_status || null,
    CreatedBy:                 row.created_by     || null,
    CreatedAt:                 row.created_at     || null,
    GameType:                  row.game_type || "Pokemon",
    LocationID:                row.location_id || null,
    Category:                  row.category || null,
    MarketPrice:               row.market_price ? parseFloat(row.market_price) : null,
    MarketPriceUpdatedAt:      row.market_price_updated_at || null,
    MarketPriceSource:         row.market_price_source || null,
    MarketPriceData:           row.market_price_data || null,
    TcggoId:                   row.tcggo_id || null,
    LorcanaInkColor:           row.lorcana_ink_color || "",
    LorcanaCharacterVersion:   row.lorcana_character_version || "",
    LorcanaProperty:           row.lorcana_property || "",
    LorcanaFoilType:           row.lorcana_foil_type || "Normal",
    ConsignorID:               row.consignor_id || "",
    MinAcceptPrice:            row.min_accept_price ? parseFloat(row.min_accept_price) : "",
    TradesAccepted:            row.trades_accepted || false,
    ImportBatchID:             row.import_batch_id || "",
    ImportSource:              row.import_source    || "",
    AssignedBuyerID:           row.assigned_buyer_id || "",
    BuyerNotifiedAt:           row.buyer_notified_at || "",
    CreatedAt:                 row.created_at,
  };
}

function toDB(item) {
  return {
    type:                        item.Type        || "Single Card",
    card_name:                   (item.CardName  || "").trim() || null,
    set_name:                    (item.Set        || "").trim() || null,
    card_number:                 (item.Number     || "").trim() || null,
    rarity:                      item.Rarity      || null,
    language:                    item.Language    || "English",
    condition:                   item.Condition   || "Near Mint",
    variant:                     item.Variant     || null,
    image_urls:                  item.ImageURLs   && item.ImageURLs.length ? item.ImageURLs : null,
    custom_category:             item.CustomCategory || null,
    item_type:                   item.ItemType    || null,
    description:                 item.Description || null,
    tags:                        item.Tags && item.Tags.length ? item.Tags : null,
    product_type:                item.ProductType || null,
    grade:                       item.Grade       || null,
    quantity:                    item.Quantity    || 1,
    source:                      item.Source      || "Individual Buy",
    acquisition_cost:            item.AcquisitionCost != null && item.AcquisitionCost !== "" ? parseFloat(item.AcquisitionCost) : null,
    list_price:                  item.ListPrice != null && item.ListPrice !== "" ? parseFloat(item.ListPrice) : null,
    min_sell_price:              item.MinSellPrice               ? parseFloat(item.MinSellPrice)               : null,
    market_value_at_acquisition: item.MarketValueAtAcquisition  ? parseFloat(item.MarketValueAtAcquisition)  : null,
    acquisition_date:            item.AcquisitionDate            || null,
    lot_id:                      item.LotID       || null,
    lot_name:                    item.LotName     || null,
    lot_item_number:             item.LotItemNumber ? parseInt(item.LotItemNumber) : null,
    show_id:                     item.ShowID      || null,
    show_name:                   item.ShowName    || null,
    ...(item.AcquiredShowID ? { acquired_show_id: item.AcquiredShowID } : {}),
    image_url:                   item.ImageURL    || null,
    qr_code:                     item.QRCode      || null,
    status:                      item.Status      || "In Stock",
    sold_price:                  item.SoldPrice   ? parseFloat(item.SoldPrice)   : null,
    sold_date:                   item.SoldDate    || null,
    sold_at:                     item.SoldAt      || null,
    sold_channel:                item.SoldChannel || null,
    buyer_gender:                item.BuyerGender || null,
    trade_credit:                item.TradeCredit ? parseFloat(item.TradeCredit) : null,
    grading_company:             item.GradingCompany    || null,
    grading_sent_date:           item.GradingSentDate   || null,
    grading_return_date:         item.GradingReturnDate || null,
    grading_estimated_value:     item.GradingEstimatedValue ? parseFloat(item.GradingEstimatedValue) : null,
    grading_cost:                item.GradingCost        ? parseFloat(item.GradingCost)        : null,
    notes:                       item.Notes       || null,
    is_consignment:              item.IsConsignment || false,
    is_online:                   item.IsOnline      || false,
    price_override:              item.PriceOverride || false,
    consign_payout_status:       item.ConsignPayoutStatus || null,
    created_by:                  item.CreatedBy     || null,
    game_type:                   item.GameType || "Pokemon",
    location_id:                 item.LocationID || null,
    category:                    item.Category || null,
    market_price:                item.MarketPrice != null ? parseFloat(item.MarketPrice) : null,
    market_price_updated_at:     item.MarketPriceUpdatedAt || null,
    market_price_source:         item.MarketPriceSource || null,
    market_price_data:           item.MarketPriceData || null,
    tcggo_id:                    item.TcggoId || null,
    lorcana_ink_color:           item.LorcanaInkColor   || null,
    lorcana_character_version:   item.LorcanaCharacterVersion || null,
    lorcana_property:            item.LorcanaProperty   || null,
    lorcana_foil_type:           item.LorcanaFoilType   || "Normal",
    consignor_id:                item.ConsignorID || null,
    min_accept_price:            item.MinAcceptPrice ? parseFloat(item.MinAcceptPrice) : null,
    trades_accepted:             item.TradesAccepted || false,
    import_batch_id:             item.ImportBatchID || null,
    import_source:               item.ImportSource   || null,
    assigned_buyer_id:           item.AssignedBuyerID || null,
    buyer_notified_at:           item.BuyerNotifiedAt || null,
  };
}

function lotToApp(row) {
  return {
    ID:               row.id,
    Name:             row.name,
    Source:           row.source,
    Date:             row.date,
    TotalPaid:        parseFloat(row.total_paid)         || 0,
    TotalMarketValue: parseFloat(row.total_market_value) || 0,
    AllocationRatio:  parseFloat(row.allocation_ratio)   || 0,
    CostMethod:       row.cost_method || "even",
    CardCount:        row.card_count  || 0,
    PackCount:        row.pack_count  || 0,
    CostPerCard:      parseFloat(row.cost_per_card)      || 0,
    MinSellPrice:     parseFloat(row.min_sell_price)     || 0,
    Status:           row.status      || "Open",
    Notes:            row.notes       || "",
    CreatedAt:        row.created_at,
  };
}

function showToApp(row) {
  return {
    ID:         row.id,
    Name:       row.name,
    Location:   row.location    || "",
    StartDate:  row.start_date  || "",
    EndDate:    row.end_date    || "",
    Status:     row.status      || "Upcoming",
    TableCost:  parseFloat(row.table_cost) || 0,
    Marketing:  parseFloat(row.marketing)  || 0,
    Travel:     parseFloat(row.travel)     || 0,
    Food:       parseFloat(row.food)       || 0,
    Notes:      row.notes       || "",
    CreatedAt:  row.created_at,
  };
}

function tradeToApp(row) {
  return {
    ID:               row.id,
    Date:             row.date,
    CardsOut:         row.cards_out          || "",
    CardsIn:          row.cards_in           || "",
    TotalCreditValue: parseFloat(row.total_credit_value) || 0,
    CreditRate:       parseFloat(row.credit_rate)        || 0.8,
    Type:             row.type               || "",
    Notes:            row.notes              || "",
    CashReceived:     row.cash_amount != null ? parseFloat(row.cash_amount) : null,
    CreatedAt:        row.created_at,
  };
}

// ── INVENTORY ─────────────────────────────────────────────────────────────────
export async function readInventory() {
  const vendorId = await getVendorId();
  const { data, error } = await supabase
    .from("inventory")
    .select("*")
    .eq("vendor_id", vendorId)
    .neq("status", "Deleted")
    .order("created_at", { ascending: false })
    .range(0, 4999);
  if (error) throw error;
  return (data || []).map(toApp);
}

export async function readInventoryItem(id) {
  const { data, error } = await supabase
    .from("inventory")
    .select("*")
    .eq("id", id)
    .single();
  if (error) return null;
  return toApp(data);
}

export async function readImportBatches(vendorId) {
  const { data, error } = await supabase
    .from("inventory")
    .select("import_batch_id, import_source, created_at, card_name")
    .eq("vendor_id", vendorId)
    .not("import_batch_id", "is", null)
    .order("created_at", { ascending: false })
    .limit(5000);
  if (error) throw error;
  const batches = {};
  (data || []).forEach(row => {
    const id = row.import_batch_id;
    if (!batches[id]) {
      batches[id] = { batchId: id, source: row.import_source, count: 0, createdAt: row.created_at };
    }
    batches[id].count++;
    if (row.created_at < batches[id].createdAt) batches[id].createdAt = row.created_at;
  });
  return Object.values(batches).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export async function deleteImportBatch(vendorId, batchId) {
  const { data, error } = await supabase
    .from("inventory")
    .update({ status: "Deleted" })
    .eq("vendor_id", vendorId)
    .eq("import_batch_id", batchId)
    .select("id");
  if (error) throw error;
  return data?.length || 0;
}

export async function readImportBatchCards(vendorId, batchId) {
  const { data, error } = await supabase
    .from("inventory")
    .select("card_name, set_name, card_number, condition, grading_company, grade, status, list_price, acquisition_cost, sold_price, sold_date, sold_channel")
    .eq("vendor_id", vendorId)
    .eq("import_batch_id", batchId)
    .order("card_name");
  if (error) throw error;
  return data || [];
}

export async function addInventoryItem(item) {
  const vendorId = await getVendorId();
  const { data, error } = await supabase
    .from("inventory")
    .insert({ ...toDB(item), vendor_id: vendorId })
    .select()
    .single();
  if (error) throw error;
  const saved = toApp(data);
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let qrCode = "";
  for (let i = 0; i < 6; i++) qrCode += chars[Math.floor(Math.random() * chars.length)];
  await updateInventoryItem(saved.ID, { QRCode: qrCode });
  saved.QRCode = qrCode;

  refreshSingleCardPrice(saved.ID).catch(() => {});

  return saved;
}

export async function updateInventoryItem(id, updates) {
  const dbUpdates = {};
  if (updates.Type        !== undefined) dbUpdates.type         = updates.Type;
  if (updates.CardName    !== undefined) dbUpdates.card_name    = (updates.CardName  || "").trim() || null;
  if (updates.Set         !== undefined) dbUpdates.set_name     = (updates.Set        || "").trim() || null;
  if (updates.Number      !== undefined) dbUpdates.card_number  = (updates.Number     || "").trim() || null;
  if (updates.Rarity      !== undefined) dbUpdates.rarity       = updates.Rarity;
  if (updates.Language    !== undefined) dbUpdates.language     = updates.Language;
  if (updates.Condition   !== undefined) dbUpdates.condition    = updates.Condition;
  if (updates.Variant     !== undefined) dbUpdates.variant      = updates.Variant || null;
  if (updates.ImageURLs      !== undefined) dbUpdates.image_urls      = (updates.ImageURLs && updates.ImageURLs.length) ? updates.ImageURLs : null;
  if (updates.CustomCategory !== undefined) dbUpdates.custom_category = updates.CustomCategory || null;
  if (updates.ItemType       !== undefined) dbUpdates.item_type       = updates.ItemType || null;
  if (updates.Description    !== undefined) dbUpdates.description     = updates.Description || null;
  if (updates.Tags           !== undefined) dbUpdates.tags            = (updates.Tags && updates.Tags.length) ? updates.Tags : null;
  if (updates.ProductType    !== undefined) dbUpdates.product_type    = updates.ProductType || null;
  if (updates.Grade       !== undefined) dbUpdates.grade        = updates.Grade;
  if (updates.Quantity    !== undefined) dbUpdates.quantity     = updates.Quantity;
  if (updates.Source      !== undefined) dbUpdates.source       = updates.Source;
  if (updates.AcquisitionCost !== undefined) dbUpdates.acquisition_cost = updates.AcquisitionCost != null && updates.AcquisitionCost !== "" ? parseFloat(updates.AcquisitionCost) : null;
  if (updates.ListPrice       !== undefined) dbUpdates.list_price       = updates.ListPrice       != null && updates.ListPrice       !== "" ? parseFloat(updates.ListPrice)       : null;
  if (updates.OnlinePrice     !== undefined) dbUpdates.online_price     = updates.OnlinePrice     != null && updates.OnlinePrice     !== "" ? parseFloat(updates.OnlinePrice)     : null;
  if (updates.MinSellPrice             !== undefined) dbUpdates.min_sell_price              = updates.MinSellPrice             ? parseFloat(updates.MinSellPrice)             : null;
  if (updates.TaxRate                  !== undefined) dbUpdates.tax_rate                    = updates.TaxRate                  != null ? parseFloat(updates.TaxRate)  : null;
  if (updates.TaxAmount                !== undefined) dbUpdates.tax_amount                  = updates.TaxAmount                != null ? parseFloat(updates.TaxAmount) : null;
  if (updates.MarketValueAtAcquisition !== undefined) dbUpdates.market_value_at_acquisition = updates.MarketValueAtAcquisition ? parseFloat(updates.MarketValueAtAcquisition) : null;
  if (updates.AcquisitionDate !== undefined) dbUpdates.acquisition_date = updates.AcquisitionDate || null;
  if (updates.LotID        !== undefined) dbUpdates.lot_id          = updates.LotID        || null;
  if (updates.LotName      !== undefined) dbUpdates.lot_name        = updates.LotName      || null;
  if (updates.LotItemNumber !== undefined) dbUpdates.lot_item_number = updates.LotItemNumber ? parseInt(updates.LotItemNumber) : null;
  if (updates.ShowID       !== undefined) dbUpdates.show_id         = updates.ShowID       || null;
  if (updates.ShowName     !== undefined) dbUpdates.show_name       = updates.ShowName     || null;
  if (updates.Location     !== undefined) dbUpdates.location        = updates.Location     || null;
  if (updates.ImageURL     !== undefined) dbUpdates.image_url       = updates.ImageURL     || null;
  if (updates.QRCode       !== undefined) dbUpdates.qr_code         = updates.QRCode       || null;
  if (updates.Status       !== undefined) dbUpdates.status          = updates.Status;
  if (updates.SoldPrice    !== undefined) dbUpdates.sold_price      = updates.SoldPrice    ? parseFloat(updates.SoldPrice)    : null;
  if (updates.SoldDate     !== undefined) dbUpdates.sold_date       = updates.SoldDate     || null;
  if (updates.SoldAt       !== undefined) dbUpdates.sold_at         = updates.SoldAt       || null;
  if (updates.SoldChannel  !== undefined) dbUpdates.sold_channel    = updates.SoldChannel  || null;
  if (updates.PaymentMethod !== undefined) dbUpdates.payment_method = updates.PaymentMethod || null;
  if (updates.BuyerGender  !== undefined) dbUpdates.buyer_gender    = updates.BuyerGender  || null;
  if (updates.TradeCredit  !== undefined) dbUpdates.trade_credit    = updates.TradeCredit  ? parseFloat(updates.TradeCredit)  : null;
  if (updates.TradeGain    !== undefined) dbUpdates.trade_gain      = updates.TradeGain    ? parseFloat(updates.TradeGain)    : null;
  if (updates.GradingCompany       !== undefined) dbUpdates.grading_company        = updates.GradingCompany       || null;
  if (updates.GradingSentDate      !== undefined) dbUpdates.grading_sent_date      = updates.GradingSentDate      || null;
  if (updates.GradingReturnDate    !== undefined) dbUpdates.grading_return_date    = updates.GradingReturnDate    || null;
  if (updates.GradingEstimatedValue!== undefined) dbUpdates.grading_estimated_value= updates.GradingEstimatedValue ? parseFloat(updates.GradingEstimatedValue) : null;
  if (updates.GradingCost          !== undefined) dbUpdates.grading_cost           = updates.GradingCost          ? parseFloat(updates.GradingCost)          : null;
  if (updates.Notes        !== undefined) dbUpdates.notes           = updates.Notes        || null;
  if (updates.GameType     !== undefined) dbUpdates.game_type       = updates.GameType     || "Pokemon";
  if (updates.LorcanaInkColor         !== undefined) dbUpdates.lorcana_ink_color         = updates.LorcanaInkColor         || null;
  if (updates.LorcanaCharacterVersion !== undefined) dbUpdates.lorcana_character_version = updates.LorcanaCharacterVersion || null;
  if (updates.LorcanaProperty         !== undefined) dbUpdates.lorcana_property          = updates.LorcanaProperty         || null;
  if (updates.LorcanaFoilType         !== undefined) dbUpdates.lorcana_foil_type         = updates.LorcanaFoilType         || "Normal";
  if (updates.IsConsignment      !== undefined) dbUpdates.is_consignment        = !!updates.IsConsignment;
  if (updates.IsOnline           !== undefined) dbUpdates.is_online             = !!updates.IsOnline;
  if (updates.PriceOverride      !== undefined) dbUpdates.price_override        = !!updates.PriceOverride;
  if (updates.ConsignPayoutStatus !== undefined) dbUpdates.consign_payout_status = updates.ConsignPayoutStatus || null;
  if (updates.ConsignorID     !== undefined) dbUpdates.consignor_id     = updates.ConsignorID     || null;
  if (updates.MinAcceptPrice  !== undefined) dbUpdates.min_accept_price = updates.MinAcceptPrice  ? parseFloat(updates.MinAcceptPrice) : null;
  if (updates.TradesAccepted  !== undefined) dbUpdates.trades_accepted  = !!updates.TradesAccepted;
  if (updates.AssignedBuyerID !== undefined) dbUpdates.assigned_buyer_id = updates.AssignedBuyerID || null;
  if (updates.BuyerNotifiedAt !== undefined) dbUpdates.buyer_notified_at = updates.BuyerNotifiedAt || null;
  if (updates.MarketPrice          !== undefined) dbUpdates.market_price            = updates.MarketPrice          != null ? parseFloat(updates.MarketPrice) : null;
  if (updates.MarketPriceUpdatedAt !== undefined) dbUpdates.market_price_updated_at = updates.MarketPriceUpdatedAt || null;
  if (updates.MarketPriceSource    !== undefined) dbUpdates.market_price_source     = updates.MarketPriceSource    || null;
  if (updates.MarketPriceData      !== undefined) dbUpdates.market_price_data       = updates.MarketPriceData      || null;
  if (updates.TcggoId              !== undefined) dbUpdates.tcggo_id                = updates.TcggoId              || null;

  const { data, error } = await supabase
    .from("inventory")
    .update(dbUpdates)
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return toApp(data);
}

export async function deleteInventoryItem(id) {
  const result = await updateInventoryItem(id, { Status: "Deleted" });
  await removeDealBoardListingIfListed(id);
  return result;
}

// ── LOCATIONS (Enterprise tier — multi-location inventory) ──────────────────

function locationToApp(row) {
  return {
    ID:          row.id,
    StoreName:   row.store_name,
    StoreNumber: row.store_number,
    Address:     row.address || "",
    City:        row.city || "",
    State:       row.state || "",
    Zip:         row.zip || "",
    Phone:       row.phone || "",
    KioskSlug:   row.kiosk_slug || "",
    Status:      row.status,
  };
}

export async function readLocations() {
  const vendorId = await getVendorId();
  const { data, error } = await supabase
    .from("locations")
    .select("*")
    .eq("vendor_id", vendorId)
    .neq("status", "Archived")
    .order("store_number", { ascending: true });
  if (error) throw error;
  return (data || []).map(locationToApp);
}

export async function addLocation(loc) {
  const vendorId = await getVendorId();
  const { data, error } = await supabase
    .from("locations")
    .insert({
      vendor_id:    vendorId,
      store_name:   loc.StoreName,
      store_number: loc.StoreNumber,
      address:      loc.Address || null,
      city:         loc.City || null,
      state:        loc.State || null,
      zip:          loc.Zip || null,
      phone:        loc.Phone || null,
      kiosk_slug:   loc.KioskSlug || null,
    })
    .select()
    .single();
  if (error) throw error;
  return locationToApp(data);
}

export async function updateLocation(id, patch) {
  const dbPatch = {};
  if (patch.StoreName   !== undefined) dbPatch.store_name   = patch.StoreName;
  if (patch.StoreNumber !== undefined) dbPatch.store_number = patch.StoreNumber;
  if (patch.Address     !== undefined) dbPatch.address      = patch.Address;
  if (patch.City        !== undefined) dbPatch.city         = patch.City;
  if (patch.State       !== undefined) dbPatch.state        = patch.State;
  if (patch.Zip         !== undefined) dbPatch.zip          = patch.Zip;
  if (patch.Phone       !== undefined) dbPatch.phone        = patch.Phone;
  if (patch.KioskSlug   !== undefined) dbPatch.kiosk_slug   = patch.KioskSlug;
  if (patch.Status      !== undefined) dbPatch.status       = patch.Status;
  const { error } = await supabase.from("locations").update(dbPatch).eq("id", id);
  if (error) throw error;
}

export async function archiveLocation(id) {
  return updateLocation(id, { Status: "Archived" });
}

export async function getStaffLocationIds(vendorUserId) {
  const { data, error } = await supabase
    .from("vendor_user_locations")
    .select("location_id")
    .eq("vendor_user_id", vendorUserId);
  if (error) throw error;
  return (data || []).map(r => r.location_id);
}

export async function setStaffLocations(vendorUserId, locationIds) {
  const { error: delErr } = await supabase
    .from("vendor_user_locations")
    .delete()
    .eq("vendor_user_id", vendorUserId);
  if (delErr) throw delErr;
  if (!locationIds.length) return;
  const { error: insErr } = await supabase
    .from("vendor_user_locations")
    .insert(locationIds.map(location_id => ({ vendor_user_id: vendorUserId, location_id })));
  if (insErr) throw insErr;
}

// ── CATEGORY PRICING RULES (Enterprise tier) ─────────────────────────────────

export const PRICING_CATEGORIES = ["Raw Singles", "Graded/Slabs", "Sealed", "Accessories"];

export function deriveCategory(item) {
  if (!item) return null;
  if (item.Source === "Custom Item") return "Accessories";
  if (item.Type === "Sealed Product") return "Sealed";
  if (item.GradingCompany && item.Grade) return "Graded/Slabs";
  if (item.Type === "Single Card" || item.CardName) return "Raw Singles";
  return "Accessories";
}

function categoryRuleToApp(row) {
  return {
    Category: row.category,
    Strategy: row.strategy || "average",
    Percent:  row.percent != null ? String(row.percent) : "",
    Dollar:   row.dollar  != null ? String(row.dollar)  : "",
    Ceiling:  row.ceiling != null ? row.ceiling : 100,
  };
}

export async function readCategoryPricingRules(locationId) {
  const { data, error } = await supabase
    .from("category_pricing_rules")
    .select("*")
    .eq("location_id", locationId);
  if (error) throw error;
  const byCategory = {};
  (data || []).forEach(row => { byCategory[row.category] = categoryRuleToApp(row); });
  return PRICING_CATEGORIES.map(cat => byCategory[cat] || {
    Category: cat, Strategy: "average", Percent: "10", Dollar: "5", Ceiling: 100,
  });
}

export async function saveCategoryPricingRule(locationId, rule) {
  const vendorId = await getVendorId();
  const { error } = await supabase
    .from("category_pricing_rules")
    .upsert({
      vendor_id:   vendorId,
      location_id: locationId,
      category:    rule.Category,
      strategy:    rule.Strategy || "average",
      percent:     rule.Percent !== "" ? parseFloat(rule.Percent) : null,
      dollar:      rule.Dollar  !== "" ? parseFloat(rule.Dollar)  : null,
      ceiling:     rule.Ceiling || 100,
      updated_at: new Date().toISOString(),
    }, { onConflict: "location_id,category" });
  if (error) throw error;
}

// ── LOTS ──────────────────────────────────────────────────────────────────────
export async function readLots() {
  const vendorId = await getVendorId();
  const { data, error } = await supabase
    .from("lots")
    .select("*")
    .eq("vendor_id", vendorId)
    .neq("status", "Deleted")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data || []).map(lotToApp);
}

export async function addLot(lot) {
  const vendorId = await getVendorId();
  const { data, error } = await supabase
    .from("lots")
    .insert({
      vendor_id:          vendorId,
      name:               lot.Name,
      source:             lot.Source,
      date:               lot.Date             || null,
      total_paid:         lot.TotalPaid        ? parseFloat(lot.TotalPaid)        : null,
      total_market_value: lot.TotalMarketValue ? parseFloat(lot.TotalMarketValue) : null,
      allocation_ratio:   lot.AllocationRatio  ? parseFloat(lot.AllocationRatio)  : null,
      cost_method:        lot.CostMethod       || "even",
      card_count:         lot.CardCount        ? parseInt(lot.CardCount)          : null,
      pack_count:         lot.PackCount        ? parseInt(lot.PackCount)          : null,
      cost_per_card:      lot.CostPerCard      ? parseFloat(lot.CostPerCard)      : null,
      min_sell_price:     lot.MinSellPrice     ? parseFloat(lot.MinSellPrice)     : null,
      status:             "Open",
      notes:              lot.Notes            || null,
    })
    .select()
    .single();
  if (error) throw error;
  return lotToApp(data);
}

export async function updateLot(id, updates) {
  const dbUpdates = {};
  if (updates.Name             !== undefined) dbUpdates.name               = updates.Name;
  if (updates.Status           !== undefined) dbUpdates.status             = updates.Status;
  if (updates.TotalPaid        !== undefined) dbUpdates.total_paid         = updates.TotalPaid        ? parseFloat(updates.TotalPaid)        : null;
  if (updates.TotalMarketValue !== undefined) dbUpdates.total_market_value = updates.TotalMarketValue ? parseFloat(updates.TotalMarketValue) : null;
  if (updates.AllocationRatio  !== undefined) dbUpdates.allocation_ratio   = updates.AllocationRatio  ? parseFloat(updates.AllocationRatio)  : null;
  if (updates.CostMethod       !== undefined) dbUpdates.cost_method        = updates.CostMethod;
  if (updates.Notes            !== undefined) dbUpdates.notes              = updates.Notes || null;

  const { data, error } = await supabase
    .from("lots")
    .update(dbUpdates)
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return lotToApp(data);
}

// ── CONSIGNORS ────────────────────────────────────────────────────────────────
function consignorToApp(row) {
  return {
    ID:        row.id,
    FullName:  row.full_name,
    Email:     row.email || "",
    Phone:     row.phone || "",
    Notes:     row.notes || "",
    CreatedAt: row.created_at,
  };
}

export async function readConsignors() {
  const vendorId = await getVendorId();
  const { data, error } = await supabase
    .from("consignors")
    .select("*")
    .eq("vendor_id", vendorId)
    .order("full_name", { ascending: true });
  if (error) throw error;
  return (data || []).map(consignorToApp);
}

export async function addConsignor(consignor) {
  const vendorId = await getVendorId();
  const { data, error } = await supabase
    .from("consignors")
    .insert({
      vendor_id: vendorId,
      full_name: consignor.FullName,
      email:     consignor.Email || null,
      phone:     consignor.Phone || null,
      notes:     consignor.Notes || null,
    })
    .select()
    .single();
  if (error) throw error;
  return consignorToApp(data);
}

export async function updateConsignor(id, updates) {
  const dbUpdates = {};
  if (updates.FullName !== undefined) dbUpdates.full_name = updates.FullName;
  if (updates.Email    !== undefined) dbUpdates.email     = updates.Email || null;
  if (updates.Phone    !== undefined) dbUpdates.phone     = updates.Phone || null;
  if (updates.Notes    !== undefined) dbUpdates.notes     = updates.Notes || null;
  dbUpdates.updated_at = new Date().toISOString();

  const { data, error } = await supabase
    .from("consignors")
    .update(dbUpdates)
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return consignorToApp(data);
}

export async function deleteConsignor(id) {
  const { error } = await supabase
    .from("consignors")
    .delete()
    .eq("id", id);
  if (error) throw error;
}

// ── SHOWS ─────────────────────────────────────────────────────────────────────
export async function readShows() {
  const vendorId = await getVendorId();
  const { data, error } = await supabase
    .from("shows")
    .select("*")
    .eq("vendor_id", vendorId)
    .neq("status", "Deleted")
    .order("start_date", { ascending: false });
  if (error) throw error;
  return (data || []).map(showToApp);
}

export async function addShow(show) {
  const vendorId = await getVendorId();
  const { data, error } = await supabase
    .from("shows")
    .insert({
      vendor_id:  vendorId,
      name:       show.Name,
      location:   show.Location   || null,
      start_date: show.StartDate  || null,
      end_date:   show.EndDate    || null,
      status:     show.Status     || "Upcoming",
      table_cost: show.TableCost  ? parseFloat(show.TableCost)  : null,
      marketing:  show.Marketing  ? parseFloat(show.Marketing)  : null,
      travel:     show.Travel     ? parseFloat(show.Travel)     : null,
      food:       show.Food       ? parseFloat(show.Food)       : null,
      notes:      show.Notes      || null,
    })
    .select()
    .single();
  if (error) throw error;
  return showToApp(data);
}

export async function updateShow(id, updates) {
  const dbUpdates = {};
  if (updates.Name       !== undefined) dbUpdates.name       = updates.Name;
  if (updates.Location   !== undefined) dbUpdates.location   = updates.Location   || null;
  if (updates.StartDate  !== undefined) dbUpdates.start_date = updates.StartDate  || null;
  if (updates.EndDate    !== undefined) dbUpdates.end_date   = updates.EndDate    || null;
  if (updates.Status     !== undefined) dbUpdates.status     = updates.Status;
  if (updates.TableCost  !== undefined) dbUpdates.table_cost = updates.TableCost  ? parseFloat(updates.TableCost)  : null;
  if (updates.Marketing  !== undefined) dbUpdates.marketing  = updates.Marketing  ? parseFloat(updates.Marketing)  : null;
  if (updates.Travel     !== undefined) dbUpdates.travel     = updates.Travel     ? parseFloat(updates.Travel)     : null;
  if (updates.Food       !== undefined) dbUpdates.food       = updates.Food       ? parseFloat(updates.Food)       : null;
  if (updates.Notes      !== undefined) dbUpdates.notes      = updates.Notes      || null;

  const { data, error } = await supabase
    .from("shows")
    .update(dbUpdates)
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return showToApp(data);
}

// ── SHOW ITEMS ────────────────────────────────────────────────────────────────
export async function readShowItems() {
  const vendorId = await getVendorId();
  const { data, error } = await supabase
    .from("show_items")
    .select("*")
    .eq("vendor_id", vendorId);
  if (error) throw error;
  return (data || []).map(r => ({
    ID:          r.id,
    ShowID:      r.show_id,
    InventoryID: r.inventory_id,
    AddedDate:   r.added_date,
  }));
}

export async function addShowItem(showId, inventoryId) {
  const vendorId = await getVendorId();
  const { data, error } = await supabase
    .from("show_items")
    .insert({ vendor_id: vendorId, show_id: showId, inventory_id: inventoryId })
    .select()
    .single();
  if (error && error.code !== "23505") throw error;
  return data;
}

export async function removeShowItem(showId, inventoryId) {
  const { error } = await supabase
    .from("show_items")
    .delete()
    .eq("show_id", showId)
    .eq("inventory_id", inventoryId);
  if (error) throw error;
}

// ── TRADES ────────────────────────────────────────────────────────────────────
export async function readTrades() {
  const vendorId = await getVendorId();
  const { data, error } = await supabase
    .from("trades")
    .select("*")
    .eq("vendor_id", vendorId)
    .order("date", { ascending: false });
  if (error) throw error;
  return (data || []).map(tradeToApp);
}

export async function addTrade(trade) {
  const vendorId = await getVendorId();
  const { data, error } = await supabase
    .from("trades")
    .insert({
      vendor_id:          vendorId,
      date:               trade.Date               || null,
      cards_out:          trade.CardsOut            || null,
      cards_in:           trade.CardsIn             || null,
      total_credit_value: trade.TotalCreditValue    ? parseFloat(trade.TotalCreditValue) : null,
      credit_rate:        trade.CreditRate          ? parseFloat(trade.CreditRate)        : null,
      type:               trade.Type               || null,
      notes:              trade.Notes              || null,
      cash_amount:        trade.CashReceived != null ? parseFloat(trade.CashReceived) : null,
    })
    .select()
    .single();
  if (error) throw error;
  return tradeToApp(data);
}

export async function updateTrade(id, updates) {
  const dbUpdates = {};
  if (updates.Date             !== undefined) dbUpdates.date               = updates.Date             || null;
  if (updates.CardsOut         !== undefined) dbUpdates.cards_out          = updates.CardsOut         || null;
  if (updates.CardsIn          !== undefined) dbUpdates.cards_in           = updates.CardsIn          || null;
  if (updates.TotalCreditValue !== undefined) dbUpdates.total_credit_value = updates.TotalCreditValue ? parseFloat(updates.TotalCreditValue) : null;
  if (updates.Notes            !== undefined) dbUpdates.notes              = updates.Notes            || null;

  const { data, error } = await supabase
    .from("trades")
    .update(dbUpdates)
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return tradeToApp(data);
}

// ── REAL-TIME SUBSCRIPTIONS ───────────────────────────────────────────────────
export function subscribeToInventory(vendorId, callback) {
  return supabase
    .channel("inventory_changes")
    .on("postgres_changes", {
      event:  "*",
      schema: "public",
      table:  "inventory",
      filter: `vendor_id=eq.${vendorId}`,
    }, payload => callback(payload))
    .subscribe();
}

export function subscribeToDealBoardListings(callback) {
  return supabase
    .channel(`deal_board_listings_${Date.now()}`)
    .on("postgres_changes", {
      event:  "*",
      schema: "public",
      table:  "deal_board_listings",
    }, payload => callback(payload))
    .subscribe();
}

export function subscribeToAll(vendorId, callbacks) {
  const channelName = `vendorstax_${vendorId}_${Date.now()}`;
  const channel = supabase.channel(channelName);

  const tables = [
    { table: "inventory",  fn: callbacks.inventory                    },
    { table: "lots",       fn: callbacks.lots                         },
    { table: "shows",      fn: callbacks.shows                        },
    { table: "trades",     fn: callbacks.trades                       },
    { table: "show_items", fn: callbacks.shows,    noFilter: true     },
    { table: "claims",     fn: callbacks.claims                       },
  ];

  tables.forEach(({ table, fn, noFilter }) => {
    if (!fn) return;
    const config = {
      event:  "*",
      schema: "public",
      table,
    };
    if (!noFilter) config.filter = `vendor_id=eq.${vendorId}`;
    channel.on("postgres_changes", config, payload => fn({...payload, table}));
  });

  return channel.subscribe((status, err) => {
    console.log("VendorStax Realtime:", status, err || "");
    if (status === "CHANNEL_ERROR") {
      console.error("Realtime channel error — check Supabase Replication settings");
    }
  });
}

// ── PRICE HISTORY ────────────────────────────────────────────────────────────
export async function addPriceHistory(entries) {
  const vendorId = await getVendorId();
  const rows = entries.map(e => ({
    vendor_id:    vendorId,
    inventory_id: e.inventory_id,
    card_name:    e.card_name    || null,
    set_name:     e.set_name     || null,
    card_number:  e.card_number  || null,
    market_value: parseFloat(e.market_value),
    source:       e.source       || "manual",
    recorded_at:  e.recorded_at  || new Date().toISOString(),
  }));
  const { error } = await supabase.from("price_history").insert(rows);
  if (error) throw error;
}

export async function getPriceHistory(inventoryId, limit = 10) {
  const { data, error } = await supabase
    .from("price_history")
    .select("*")
    .eq("inventory_id", inventoryId)
    .order("recorded_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

export async function getAllPriceHistory(vendorId) {
  const { data, error } = await supabase
    .from("price_history")
    .select("*")
    .eq("vendor_id", vendorId)
    .order("recorded_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function logListPriceChange(item, oldListPrice, newListPrice) {
  if (!item?.ID || !newListPrice || oldListPrice === newListPrice) return;
  const vendorId = await getVendorId();
  const { error } = await supabase.from("price_history").insert({
    vendor_id:    vendorId,
    inventory_id: item.ID,
    card_name:    item.CardName || null,
    set_name:     item.Set      || null,
    card_number:  item.Number   || null,
    market_value: parseFloat(newListPrice),
    source:       "manual_update",
    recorded_at:  new Date().toISOString(),
  });
  if (error) console.warn("Price log failed:", error.message);
}

export async function searchCardHistory(cardName, cardNumber, setName) {
  if (!cardName?.trim()) return { priceHistory: [], soldHistory: [], ownedCards: [] };
  const vendorId = await getVendorId();
  const term = cardName.trim().toLowerCase();

  let invQuery = supabase
    .from("inventory")
    .select("id,card_name,set_name,card_number,condition,acquisition_cost,list_price,sold_price,sold_date,status,source")
    .eq("vendor_id", vendorId)
    .ilike("card_name", `%${term}%`);

  if (cardNumber?.trim()) {
    invQuery = invQuery.ilike("card_number", `%${cardNumber.trim()}%`);
  }
  if (setName?.trim()) {
    invQuery = invQuery.ilike("set_name", `%${setName.trim()}%`);
  }

  const [phRes, invRes] = await Promise.all([
    supabase
      .from("price_history")
      .select("*")
      .eq("vendor_id", vendorId)
      .ilike("card_name", `%${term}%`)
      .order("recorded_at", { ascending: true }),
    invQuery,
  ]);

  return {
    priceHistory: phRes.data  || [],
    soldHistory:  (invRes.data || []).filter(i => i.status === "Sold" && i.sold_price),
    ownedCards:   (invRes.data || []).filter(i => i.status !== "Sold" && i.status !== "Deleted"),
  };
}

// ── SHOPPING LISTS ────────────────────────────────────────────────────────────
export async function readShoppingLists() {
  const vendorId = await getVendorId();
  if (!vendorId) return [];
  const { data, error } = await supabase
    .from("shopping_lists")
    .select("*")
    .eq("vendor_id", vendorId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data || []).map(row => ({
    id:        row.id,
    name:      row.name,
    items:     row.items || [],
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }));
}

export async function addShoppingList(name) {
  const vendorId = await getVendorId();
  const { data, error } = await supabase
    .from("shopping_lists")
    .insert([{ vendor_id: vendorId, name, items: [] }])
    .select()
    .single();
  if (error) throw error;
  return { id: data.id, name: data.name, items: [], createdAt: data.created_at };
}

export async function updateShoppingList(id, name, items) {
  const { error } = await supabase
    .from("shopping_lists")
    .update({ name, items, updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}

export async function deleteShoppingList(id) {
  const { error } = await supabase
    .from("shopping_lists")
    .delete()
    .eq("id", id);
  if (error) throw error;
}

// ── PKMNPRICES MARKET PRICING ─────────────────────────────────────────────────
const PRICING_PROXY = "/pkmnprices-pricing";

const APITCG_PROXY = "/apitcg-pricing";
const APITCG_TCG_SLUGS = { "Magic": "magic", "One Piece": "one-piece", "Yu-Gi-Oh": "yugioh", "Lorcana": "lorcana", "Pokemon": "pokemon" };

async function pricingFetchHeaders() {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      return { "Authorization": `Bearer ${session.access_token}` };
    }
  } catch {}
  return {};
}

const FRESH_WINDOW_MS = 24 * 60 * 60 * 1000;

const MIN_SALES_FOR_7D = 3;

function round2(n) {
  return Math.round(n * 100) / 100;
}

export function computeTax(soldPrice, vendor) {
  if (!vendor?.tax_tracking_enabled) return { TaxRate: null, TaxAmount: null };
  const rate = parseFloat(vendor.tax_rate) || 0;
  const price = parseFloat(soldPrice) || 0;
  return { TaxRate: rate, TaxAmount: round2(price * (rate / 100)) };
}

// ── Price Update Log — what the nightly job actually changed ────────────────

export async function getUnseenPriceUpdates(vendor) {
  if (!vendor?.id) return [];
  const since = vendor.price_update_last_seen_at || "1970-01-01T00:00:00Z";
  const { data, error } = await supabase
    .from("price_update_log")
    .select("*")
    .eq("vendor_id", vendor.id)
    .gt("created_at", since)
    .order("created_at", { ascending: false });
  if (error) return [];
  return (data || []).map(r => ({
    ID: r.id, InventoryID: r.inventory_id, CardName: r.card_name,
    OldPrice: r.old_price != null ? parseFloat(r.old_price) : null,
    NewPrice: parseFloat(r.new_price), CreatedAt: r.created_at,
  }));
}

export async function markPriceUpdatesSeen(vendorId) {
  const { error } = await supabase
    .from("vendors")
    .update({ price_update_last_seen_at: new Date().toISOString() })
    .eq("id", vendorId);
  if (error) throw error;
}

export async function checkNearingCapEmail() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) return;
  try {
    await fetch("/send-nearing-cap-email", {
      method: "POST",
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
  } catch (e) {  }
}

export async function logManualPriceEdit(vendorId, inventoryId, oldPrice, newPrice) {
  if (!vendorId) return;
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) return;
  try {
    await fetch("/log-manual-price-edit", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ inventoryId, oldPrice, newPrice }),
    });
  } catch (e) {  }
}

// ── FALLBACK RANGE ────────────────────────────────────────────────────────────
const PRICE_RANGE_TIERS = [
  { max:    10, pct: 0.50 },
  { max:    25, pct: 0.30 },
  { max:    50, pct: 0.20 },
  { max:   100, pct: 0.15 },
  { max:   200, pct: 0.12 },
  { max:   400, pct: 0.10 },
  { max:   600, pct: 0.08 },
  { max:   800, pct: 0.07 },
  { max:  1000, pct: 0.06 },
  { max:  1500, pct: 0.05 },
  { max: Infinity, pct: 0.04 },
];
function getPriceRangePct(center) {
  return (PRICE_RANGE_TIERS.find(t => center <= t.max) || PRICE_RANGE_TIERS.at(-1)).pct;
}
function buildFallbackRange(center, source, currency = "USD") {
  const pct   = getPriceRangePct(center);
  const delta = center * pct;
  return {
    center:    round2(center),
    low:       round2(center - delta),
    high:      round2(center + delta),
    saleCount: 0,
    period:    "estimated",
    source,
    currency,
    updatedAt: new Date().toISOString(),
  };
}

export function appendPriceHistory(existingData, newRange) {
  const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
  const existing = existingData || { history: [] };
  const history  = (existing.history || [])
    .filter(h => new Date(h.updatedAt).getTime() > thirtyDaysAgo);

  const last = history[history.length - 1];
  const changed = !last || Math.abs(newRange.center - last.center) / last.center > 0.01;
  if (changed) {
    history.push({
      center:    newRange.center,
      low:       newRange.low,
      high:      newRange.high,
      source:    newRange.source,
      updatedAt: newRange.updatedAt,
    });
  }

  return {
    center:    newRange.center,
    low:       newRange.low,
    high:      newRange.high,
    source:    newRange.source,
    currency:  newRange.currency,
    updatedAt: newRange.updatedAt,
    _noConditionPricing: !!newRange._noConditionPricing,
    history,
  };
}

function normalizeCardNumber(n) {
  if (n == null) return null;
  const str = String(n).split("/")[0].trim();
  return /^\d+$/.test(str) ? String(parseInt(str, 10)) : str.toUpperCase();
}


function findBestMatch(results, cardName, cardNumber, setName) {
  const normTarget = normalizeCardNumber(cardNumber);
  const setTarget  = (setName || "").trim().toLowerCase();
  const nameTarget = (cardName || "").trim().toLowerCase();

  if (normTarget && setTarget) {
    const match = results.find(c =>
      normalizeCardNumber(c.number) === normTarget &&
      (c.set?.name || "").toLowerCase() === setTarget
    );
    if (match) return match;
  }

  if (normTarget) {
    const numMatches = results.filter(c => normalizeCardNumber(c.number) === normTarget);
    if (numMatches.length === 1) return numMatches[0];

    if (numMatches.length > 1 && nameTarget) {
      const looseMatches = numMatches.filter(c => {
        const n = (c.name || "").toLowerCase();
        return n.includes(nameTarget) || nameTarget.includes(n);
      });
      if (looseMatches.length === 1) return looseMatches[0];
    }
  }

  const nameMatches = results.filter(c => (c.name||"").toLowerCase() === nameTarget);
  if (nameMatches.length === 1) return nameMatches[0];

  if (nameTarget) {
    const looseNameMatches = results.filter(c => {
      const n = (c.name || "").toLowerCase();
      return n.includes(nameTarget) || nameTarget.includes(n);
    });
    if (looseNameMatches.length === 1) return looseNameMatches[0];
  }

  return null;
}

// ── Real sold-comp range from PkmnPrices' daily history endpoint ────────────
export function summarizeHistory(rawRows, condition, variant) {
  let rows = (rawRows || []).filter(r => !r.currency || r.currency === "USD");
  if (condition) rows = rows.filter(r => (r.condition || "").toLowerCase() === condition.toLowerCase());
  if (variant) {
    const variantRows = rows.filter(r => (r.variant || "").toLowerCase() === variant.toLowerCase());
    if (variantRows.length) rows = variantRows;
  }
  if (!rows.length) return null;
  const distinctDates = new Set(rows.map(r => r.date));
  const distinctAvgs = new Set(rows.map(r => r.avg));
  if (distinctDates.size >= 3 && distinctAvgs.size === 1) return null;
  const totalSales = rows.reduce((s, r) => s + (r.sale_count || 0), 0);
  const low  = Math.min(...rows.map(r => r.low));
  const high = Math.max(...rows.map(r => r.high));
  const center = totalSales > 0
    ? rows.reduce((s, r) => s + r.avg * (r.sale_count || 0), 0) / totalSales
    : rows.reduce((s, r) => s + r.avg, 0) / rows.length;
  const asOfDate = rows.reduce((latest, r) => (!latest || r.date > latest) ? r.date : latest, null);
  return { center: round2(center), low: round2(low), high: round2(high), saleCount: totalSales, asOfDate };
}

async function fetchRawHistory(pkmnpricesId, period) {
  const headers = await pricingFetchHeaders();
  const firstRes = await fetch(`${PRICING_PROXY}?action=history&id=${encodeURIComponent(pkmnpricesId)}&period=${period}&page=1`, { headers });
  if (!firstRes.ok) return [];
  const firstJson = await firstRes.json();
  let rows = firstJson?.data || [];
  const totalPages = firstJson?.pagination?.total_pages || 1;
  for (let page = 2; page <= totalPages; page++) {
    const res = await fetch(`${PRICING_PROXY}?action=history&id=${encodeURIComponent(pkmnpricesId)}&period=${period}&page=${page}`, { headers });
    if (!res.ok) break;
    const json = await res.json();
    rows = rows.concat(json?.data || []);
  }
  return rows;
}

async function fetchHistoryRange(pkmnpricesId, period, condition, variant) {
  const rows = await fetchRawHistory(pkmnpricesId, period);
  return summarizeHistory(rows, condition, variant);
}

async function getSoldCompRange(pkmnpricesId, condition = "Near Mint", variant) {
  const summary7 = await fetchHistoryRange(pkmnpricesId, "7d", condition, variant);
  if (summary7 && summary7.saleCount >= MIN_SALES_FOR_7D) {
    return { ...summary7, period: "7d", source: "7d sold comps", currency: "USD", condition, updatedAt: new Date().toISOString() };
  }
  const summary30 = await fetchHistoryRange(pkmnpricesId, "30d", condition, variant);
  if (summary30) {
    return { ...summary30, period: "30d", source: "30d sold comps", currency: "USD", condition, updatedAt: new Date().toISOString() };
  }
  return null;
}

// ── Graded cards — real eBay sold listings for the specific grade ───────────
async function getGradedRange(pkmnpricesId, gradingCompany, grade) {
  const gradeStr = String(grade || "").replace(/\D/g, "");
  if (!gradeStr) return null;
  const headers = await pricingFetchHeaders();
  const qs = new URLSearchParams({ grader: (gradingCompany||"").toUpperCase(), grade: gradeStr, limit: "20", sort: "date_desc" });
  const res = await fetch(`${PRICING_PROXY}?action=ebay-listings&id=${encodeURIComponent(pkmnpricesId)}&${qs}`, { headers });
  if (!res.ok) return null;
  const json = await res.json();
  const rows = json?.data || [];
  const prices = rows.map(r => parseFloat(r.price)).filter(p => p > 0);
  if (!prices.length) return null;
  const center = prices.reduce((s,p)=>s+p,0) / prices.length;
  return {
    center:    round2(center),
    low:       round2(Math.min(...prices)),
    high:      round2(Math.max(...prices)),
    saleCount: prices.length,
    period:    "recent sold listings",
    source:    `Recent sold listings — ${(gradingCompany||"").toUpperCase()} ${grade}`,
    currency:  "USD",
    updatedAt: new Date().toISOString(),
    _rawRows: rows,
  };
}

async function fetchCardBasics(pkmnpricesId) {
  const headers = await pricingFetchHeaders();
  const res = await fetch(`${PRICING_PROXY}?action=card&id=${encodeURIComponent(pkmnpricesId)}&currency=usd`, { headers });
  if (!res.ok) return null;
  const card = await res.json();
  const nm = (card?.prices || []).find(p => p.condition === "Near Mint") || card?.prices?.[0];
  return {
    imageUrl:    card?.image_url || null,
    marketPrice: nm?.market_price != null ? parseFloat(nm.market_price) : null,
  };
}

async function resolveCardPriceRange({ cardName, cardNumber, setName, pkmnpricesId, gradingCompany, grade, condition, variant }) {
  if (!pkmnpricesId && (!cardName?.trim() || !cardNumber?.trim())) return null;

  let id = pkmnpricesId;

  if (!id) {
    const headers = await pricingFetchHeaders();
    const qs = new URLSearchParams({ name: cardName.trim(), per_page: "20" });
    const res = await fetch(`${PRICING_PROXY}?action=search&${qs}`, { headers });
    if (!res.ok) return null;
    const json = await res.json();
    const results = json?.data || [];
    const match = findBestMatch(results, cardName, cardNumber, setName);
    if (!match) return null;
    id = match.id;
  }

  const basics = await fetchCardBasics(id);

  const range = (gradingCompany && grade)
    ? (await getGradedRange(id, gradingCompany, grade)) || (await getSoldCompRange(id, condition || "Near Mint", variant))
    : await getSoldCompRange(id, condition || "Near Mint", variant);

  if (range) return { ...range, pkmnpricesId: id, imageUrl: basics?.imageUrl || null };

  if (basics?.marketPrice > 0) {
    return { ...buildFallbackRange(basics.marketPrice, "Estimated market price"), pkmnpricesId: id, imageUrl: basics.imageUrl, condition: condition || "Near Mint" };
  }

  return null;
}

export async function pkmnpricesFetchPriceData(pkmnpricesId) {
  if (IS_DEMO_PAGE) return { pkmnpricesId, rows7d: [], rows30d: [], basics: null };
  const [rows7d, rows30d, basics] = await Promise.all([
    fetchRawHistory(pkmnpricesId, "7d"),
    fetchRawHistory(pkmnpricesId, "30d"),
    fetchCardBasics(pkmnpricesId),
  ]);
  return { pkmnpricesId, rows7d, rows30d, basics };
}

export function computeConditionPrice(priceData, condition, variant) {
  if (!priceData) return null;
  const cond = condition || "Near Mint";
  const summary7 = summarizeHistory(priceData.rows7d, cond, variant);
  if (summary7 && summary7.saleCount >= MIN_SALES_FOR_7D) {
    return { ...summary7, period: "7d", source: "7d sold comps", currency: "USD", condition: cond,
      pkmnpricesId: priceData.pkmnpricesId, imageUrl: priceData.basics?.imageUrl || null, updatedAt: new Date().toISOString() };
  }
  const summary30 = summarizeHistory(priceData.rows30d, cond, variant);
  if (summary30) {
    return { ...summary30, period: "30d", source: "30d sold comps", currency: "USD", condition: cond,
      pkmnpricesId: priceData.pkmnpricesId, imageUrl: priceData.basics?.imageUrl || null, updatedAt: new Date().toISOString() };
  }
  if (priceData.basics?.marketPrice > 0) {
    return { ...buildFallbackRange(priceData.basics.marketPrice, "Estimated market price"), condition: cond,
      pkmnpricesId: priceData.pkmnpricesId, imageUrl: priceData.basics?.imageUrl || null };
  }
  return null;
}

export function getAvailableVariants(priceData) {
  if (!priceData) return [];
  const all = [...(priceData.rows7d || []), ...(priceData.rows30d || [])];
  const seen = new Map();
  for (const row of all) {
    const v = (row.variant || "").trim();
    if (v && !seen.has(v.toLowerCase())) seen.set(v.toLowerCase(), v);
  }
  return [...seen.values()];
}

export function getPriceHistoryView(priceData, condition, variant) {
  if (!priceData) return null;

  const filterRows = (rows) => {
    let filtered = (rows || []).filter(r => !r.currency || r.currency === "USD");
    if (variant) {
      const variantRows = filtered.filter(r => (r.variant || "").toLowerCase() === variant.toLowerCase());
      if (variantRows.length) filtered = variantRows;
    }
    return filtered;
  };

  const buildConditionTable = (rows) => {
    const byCondition = {};
    for (const r of rows) {
      const c = r.condition;
      if (!c) continue;
      if (!byCondition[c]) byCondition[c] = { totalWeighted: 0, totalSales: 0, sum: 0, count: 0 };
      const bucket = byCondition[c];
      const sales = r.sale_count || 0;
      bucket.totalWeighted += r.avg * sales;
      bucket.totalSales += sales;
      bucket.sum += r.avg;
      bucket.count += 1;
    }
    const table = {};
    for (const [c, b] of Object.entries(byCondition)) {
      table[c] = round2(b.totalSales > 0 ? b.totalWeighted / b.totalSales : b.sum / b.count);
    }
    return table;
  };

  const buildChartSeries = (rows) => {
    const targetCondition = (condition || "Near Mint").toLowerCase();
    const matching = rows.filter(r => (r.condition || "").toLowerCase() === targetCondition);
    const byDate = {};
    for (const r of matching) {
      if (!r.date) continue;
      if (!byDate[r.date]) byDate[r.date] = { totalWeighted: 0, totalSales: 0, sum: 0, count: 0 };
      const bucket = byDate[r.date];
      const sales = r.sale_count || 0;
      bucket.totalWeighted += r.avg * sales;
      bucket.totalSales += sales;
      bucket.sum += r.avg;
      bucket.count += 1;
    }
    return Object.entries(byDate)
      .map(([date, b]) => ({
        date,
        price: round2(b.totalSales > 0 ? b.totalWeighted / b.totalSales : b.sum / b.count),
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  };

  const rows7d  = filterRows(priceData.rows7d);
  const rows30d = filterRows(priceData.rows30d);

  return {
    periods: {
      "7d":  rows7d.length  ? { conditions: buildConditionTable(rows7d),  chart: buildChartSeries(rows7d)  } : null,
      "30d": rows30d.length ? { conditions: buildConditionTable(rows30d), chart: buildChartSeries(rows30d) } : null,
    },
  };
}

export async function computeGradedPrice(pkmnpricesId, gradingCompany, grade) {
  if (IS_DEMO_PAGE) return null;
  const range = await getGradedRange(pkmnpricesId, gradingCompany, grade);
  if (!range) return null;
  return { ...range, pkmnpricesId, gradingCompany: (gradingCompany || "").toUpperCase(), grade: String(grade) };
}

// ── Multi-result search (used by the Add Card drawer) ───────────────────────
export async function pkmnpricesSearch(query, number, page = 1) {
  if (!query?.trim()) return { results: [], error: null };
  if (IS_DEMO_PAGE) return { results: [], error: "demo_disabled" };
  try {
    const headers = await pricingFetchHeaders();
    const qs = new URLSearchParams({ name: query.trim(), per_page: "10", page: String(page) });
    const trimmedNumber = number?.trim().split("/")[0].trim();
    if (trimmedNumber && /^\d+$/.test(trimmedNumber)) qs.set("number", trimmedNumber);
    const res = await fetch(`${PRICING_PROXY}?action=search&${qs}`, { headers });

    if (res.status === 429) return { results: [], error: "rate_limited" };
    if (!res.ok) return { results: [], error: "request_failed" };

    const json = await res.json();
    const results = json?.data;
    if (!Array.isArray(results)) return { results: [], error: null };

    const mapped = results.map((card) => ({
      tcggoId:     card.id,
      cardName:    card.name,
      setName:     card.set?.name || null,
      cardNumber:  card.number != null ? String(card.number) : null,
      rarity:      card.rarity || null,
      imageUrl:    card.image_url || null,
      center: null, low: null, high: null, source: null, currency: null,
      price: null, priceSource: null, marketPrice: null,
    }));

    const releaseDateBySet = {};
    for (const s of CONFIG.POKEMON_SETS) releaseDateBySet[s.name] = s.released;
    mapped.sort((a, b) => {
      const da = releaseDateBySet[a.setName] || "0000-00-00";
      const db = releaseDateBySet[b.setName] || "0000-00-00";
      return db.localeCompare(da);
    });

    return { results: mapped, pagination: json?.pagination || null, error: null };
  } catch (e) {
    console.warn("PkmnPrices search failed:", query, e.message);
    return { results: [], error: "network_error" };
  }
}

// ── Per-card lookup (used by the "Get Price" button on individual cards) ────
export async function apitcgSearch(gameType, query, page = 1) {
  const tcg = APITCG_TCG_SLUGS[gameType];
  if (!tcg || !query?.trim()) return { results: [], error: null };
  if (IS_DEMO_PAGE) return { results: [], error: "demo_disabled" };
  try {
    const headers = await pricingFetchHeaders();
    const qs = new URLSearchParams({ tcg, name: query.trim(), limit: "10", page: String(page) });
    const res = await fetch(`${APITCG_PROXY}?action=search&${qs}`, { headers });

    if (res.status === 429) return { results: [], error: "rate_limited" };
    if (!res.ok) return { results: [], error: "request_failed" };

    const json = await res.json();
    const results = json?.data;
    if (!Array.isArray(results)) return { results: [], error: null };

    const mapped = results.map((card) => ({
      tcggoId:     card._id,
      cardName:    card.name,
      setName:     card.set?.name || (typeof card.set === "string" ? card.set : null),
      cardNumber:  card.code || card.attributes?.Number || (card.cardNumber != null ? String(card.cardNumber) : null),
      rarity:      card.attributes?.Rarity || null,
      imageUrl:    card.images?.[0]?.small || card.images?.[0]?.large || null,
      center: null, low: null, high: null, source: null, currency: null,
      price: null, priceSource: null, marketPrice: null,
    }));

    return { results: mapped, pagination: null, error: null };
  } catch (e) {
    console.warn("API TCG search failed:", gameType, query, e.message);
    return { results: [], error: "network_error" };
  }
}

export async function apitcgLookup(gameType, cardName, cardNumber, id) {
  const tcg = APITCG_TCG_SLUGS[gameType];
  if (!tcg) return null;
  if (IS_DEMO_PAGE) return null;
  try {
    const headers = await pricingFetchHeaders();
    let match;

    if (id) {
      const res = await fetch(`${APITCG_PROXY}?action=product&id=${encodeURIComponent(id)}`, { headers });
      if (!res.ok) return null;
      const json = await res.json();
      match = Array.isArray(json?.data) ? json.data[0] : json?.data;
      if (!match) return null;
    } else {
      if (!cardName?.trim()) return null;
      const qs = new URLSearchParams({ tcg, name: cardName.trim(), limit: "10" });
      const res = await fetch(`${APITCG_PROXY}?action=search&${qs}`, { headers });
      if (!res.ok) return null;
      const json = await res.json();
      const results = json?.data;
      if (!Array.isArray(results) || !results.length) return null;

      const getCardNumber = (c) => c.code || c.attributes?.Number || (c.cardNumber != null ? String(c.cardNumber) : null);
      match = cardNumber
        ? (results.find(c => getCardNumber(c) === String(cardNumber)) || results[0])
        : results[0];
    }

    const prices = match.markets?.tcgplayer?.prices;
    if (!prices) return null;
    const center = prices.market ?? prices.mid;
    if (center == null) return null;

    return {
      center:    round2(center),
      low:       round2(prices.low ?? center),
      high:      round2(prices.high ?? center),
      source:    "Recent market data",
      currency:  "USD",
      tcggoId:   match._id,
      imageUrl:  match.images?.[0]?.large || match.images?.[0]?.small || null,
      updatedAt: new Date().toISOString(),
      _noConditionPricing: true,
    };
  } catch (e) {
    console.warn("API TCG lookup failed:", gameType, cardName, e.message);
    return null;
  }
}

export async function apitcgSearchSealed(gameType, query, page = 1) {
  const tcg = APITCG_TCG_SLUGS[gameType];
  if (!tcg || !query?.trim()) return { results: [], error: null };
  if (IS_DEMO_PAGE) return { results: [], error: "demo_disabled" };
  try {
    const headers = await pricingFetchHeaders();
    const qs = new URLSearchParams({ tcg, type: "sealed", name: query.trim(), limit: "10", page: String(page) });
    const res = await fetch(`${APITCG_PROXY}?action=search&${qs}`, { headers });

    if (res.status === 429) return { results: [], error: "rate_limited" };
    if (!res.ok) return { results: [], error: "request_failed" };

    const json = await res.json();
    const results = json?.data;
    if (!Array.isArray(results)) return { results: [], error: null };

    const mapped = results.map((p) => ({
      tcggoId:    p._id,
      cardName:   p.name,
      setName:    p.set?.name || (typeof p.set === "string" ? p.set : null),
      description: p.attributes?.Description || null,
      imageUrl:   p.images?.[0]?.small || p.images?.[0]?.large || null,
      center: null, low: null, high: null, source: null, currency: null,
    }));

    return { results: mapped, pagination: null, error: null };
  } catch (e) {
    console.warn("API TCG sealed search failed:", gameType, query, e.message);
    return { results: [], error: "network_error" };
  }
}

export async function apitcgLookupSealed(gameType, productName, id) {
  const tcg = APITCG_TCG_SLUGS[gameType];
  if (!tcg) return null;
  if (IS_DEMO_PAGE) return null;
  try {
    const headers = await pricingFetchHeaders();
    let match;

    if (id) {
      const res = await fetch(`${APITCG_PROXY}?action=product&id=${encodeURIComponent(id)}`, { headers });
      if (!res.ok) return null;
      const json = await res.json();
      match = Array.isArray(json?.data) ? json.data[0] : json?.data;
      if (!match) return null;
    } else {
      if (!productName?.trim()) return null;
      const qs = new URLSearchParams({ tcg, type: "sealed", name: productName.trim(), limit: "10" });
      const res = await fetch(`${APITCG_PROXY}?action=search&${qs}`, { headers });
      if (!res.ok) return null;
      const json = await res.json();
      const results = json?.data;
      if (!Array.isArray(results) || !results.length) return null;
      match = results[0];
    }

    const prices = match.markets?.tcgplayer?.prices;
    if (!prices) return null;
    const center = prices.market ?? prices.mid;
    if (center == null) return null;

    return {
      center:    round2(center),
      low:       round2(prices.low ?? center),
      high:      round2(prices.high ?? center),
      source:    "Recent market data",
      currency:  "USD",
      tcggoId:   match._id,
      imageUrl:  match.images?.[0]?.large || match.images?.[0]?.small || null,
      updatedAt: new Date().toISOString(),
      _noConditionPricing: true,
    };
  } catch (e) {
    console.warn("API TCG sealed lookup failed:", gameType, productName, e.message);
    return null;
  }
}

// ── Per-card lookup (used by the "Get Price" button on individual cards) ────
export async function pkmnpricesLookup(cardName, cardNumber, setName, pkmnpricesId, gradingCompany, grade, condition) {
  if (!cardName?.trim() && !pkmnpricesId) return null;
  if (IS_DEMO_PAGE) return null;
  try {
    let id = pkmnpricesId;
    if (!id) {
      const headers = await pricingFetchHeaders();
      const qs = new URLSearchParams({ name: cardName.trim(), per_page: "20" });
      const res = await fetch(`${PRICING_PROXY}?action=search&${qs}`, { headers });
      if (!res.ok) return null;
      const json = await res.json();
      const results = json?.data || [];
      const match = findBestMatch(results, cardName, cardNumber, setName);
      if (!match) return null;
      id = match.id;
    }

    if (gradingCompany && grade) {
      const graded = await computeGradedPrice(id, gradingCompany, grade);
      if (graded) return { ...graded, tcggoId: id };
    }

    const priceData = await pkmnpricesFetchPriceData(id);
    const priced = computeConditionPrice(priceData, condition);
    if (!priced) return null;
    return { ...priced, tcggoId: id, _priceData: priceData };
  } catch (e) {
    console.warn("PkmnPrices lookup failed:", cardName, e.message);
    return null;
  }
}

export async function unifiedCardSearch(gameType, query, number, page = 1) {
  if (gameType === "Pokemon") return pkmnpricesSearch(query, number, page);
  return apitcgSearch(gameType, query, page);
}

export async function unifiedCardLookup(gameType, cardName, cardNumber, setName, pkmnpricesId, gradingCompany, grade, condition) {
  if (gameType === "Pokemon") {
    return pkmnpricesLookup(cardName, cardNumber, setName, pkmnpricesId, gradingCompany, grade, condition);
  }
  return apitcgLookup(gameType, cardName, cardNumber, pkmnpricesId);
}

// ── Price Setting: auto-pricing rules ────────────────────────────────────────
export function computeAutoPrice(range, minSellPrice, strategy, percent, dollar, ceiling = 100) {
  const reference = range?.center;
  if (reference == null || reference <= 0) return { price: null, flagged: false };
  if (reference > ceiling) return { price: null, flagged: true };

  const top = range._noConditionPricing ? reference : (range.high ?? reference);
  let price;
  switch (strategy) {
    case "top":
      price = top;
      break;
    case "top_plus_percent":
      price = top * (1 + (percent || 0) / 100);
      break;
    case "top_plus_dollar":
      price = top + (dollar || 0);
      break;
    case "top_minus_percent":
      price = top * (1 - (percent || 0) / 100);
      break;
    case "top_minus_dollar":
      price = top - (dollar || 0);
      break;
    case "average":
    default:
      price = reference;
  }
  price = Math.max(0, price);
  price = price >= 1 ? Math.round(price) : Math.max(0.25, round2(price));

  if (minSellPrice > 0 && price < minSellPrice) {
    price = Math.ceil(minSellPrice);
  }
  return { price, flagged: false };
}

// ── Refresh one card's price ──────────────────────────────────────────────────
export async function refreshSingleCardPrice(inventoryId) {
  const { data: existing, error } = await supabase
    .from("inventory")
    .select("id, vendor_id, card_name, card_number, set_name, language, condition, grading_company, grade, game_type, tcggo_id, image_url, market_price_data, status, acquisition_cost, price_override, variant, type")
    .eq("id", inventoryId)
    .single();
  if (error || !existing) return null;
  const gameType = existing.game_type || "Pokemon";
  const isSealed = existing.type === "Sealed Product";

  const range = isSealed
    ? await apitcgLookupSealed(gameType, existing.card_name, existing.tcggo_id)
    : gameType === "Pokemon"
    ? await resolveCardPriceRange({
        cardName: existing.card_name, cardNumber: existing.card_number, setName: existing.set_name,
        pkmnpricesId: existing.tcggo_id, gradingCompany: existing.grading_company, grade: existing.grade,
        condition: existing.condition || "Near Mint", variant: existing.variant || undefined,
      })
    : await apitcgLookup(gameType, existing.card_name, existing.card_number, existing.tcggo_id);
  if (!range) return null;

  const priceData = appendPriceHistory(existing.market_price_data || null, range);
  const updates = {
    market_price:            range.center,
    market_price_updated_at: range.updatedAt,
    market_price_source:     range.source,
    market_price_data:       priceData,
    tcggo_id:                range.pkmnpricesId ?? range.tcggoId,
  };
  if (range.imageUrl && !existing.image_url) updates.image_url = range.imageUrl;

  // ── Price Setting: auto-set List Price from Reference Price ──────────────
  if (existing.status === "In Stock" && !existing.price_override) {
    const { data: v } = await supabase
      .from("vendors")
      .select("dynamic_pricing_enabled, dynamic_pricing_strategy, dynamic_pricing_percent, dynamic_pricing_dollar, markup_rate, dynamic_pricing_ceiling, dynamic_pricing_addon_status, role")
      .eq("id", existing.vendor_id)
      .single();
    const hasDynamicPricingAccess = v?.role === "admin" || v?.dynamic_pricing_addon_status === "active";
    if (v?.dynamic_pricing_enabled && hasDynamicPricingAccess) {
      const minSellPrice = (parseFloat(existing.acquisition_cost) || 0) * (v.markup_rate ?? 1.25);
      const auto = computeAutoPrice(
        range, minSellPrice,
        v.dynamic_pricing_strategy || "average",
        v.dynamic_pricing_percent ?? 10,
        v.dynamic_pricing_dollar ?? 0,
        v.dynamic_pricing_ceiling ?? 100
      );
      if (auto.price != null) updates.list_price = auto.price;
    }
  }

  const { error: updateErr } = await supabase.from("inventory").update(updates).eq("id", inventoryId);
  return updateErr ? null : range;
}

export async function pkmnpricesRefreshInventory(onProgress, options = {}) {
  const { skipFresh = true, onlyWithoutPrice = false } = options;
  const vendorId = await getVendorId();

  const { data: items, error } = await supabase
    .from("inventory")
    .select("id, card_name, card_number, set_name, language, grading_company, grade, game_type, tcggo_id, market_price_updated_at, market_price_data")
    .eq("vendor_id", vendorId)
    .in("status", ["In Stock", "Reserved"]);
  if (error) throw error;
  if (!items?.length) return { updated: 0, skipped: 0, noPrice: 0, total: 0 };

  let toProcess = items.filter(i => (i.game_type || "Pokemon") === "Pokemon");

  const now = Date.now();
  if (skipFresh) {
    toProcess = toProcess.filter(i => {
      if (!i.market_price_updated_at) return true;
      return now - new Date(i.market_price_updated_at).getTime() > FRESH_WINDOW_MS;
    });
  }
  if (onlyWithoutPrice) {
    toProcess = toProcess.filter(i => !i.market_price_updated_at);
  }

  const results = [];
  let processed = 0;
  const POOL_SIZE = 4;
  let nextIndex = 0;
  const worker = async () => {
    while (nextIndex < toProcess.length) {
      const idx  = nextIndex++;
      const item = toProcess[idx];
      const range = await refreshSingleCardPrice(item.id).catch(() => null);
      if (range) results.push({ id: item.id, cardName: item.card_name, ...range });
      processed++;
      if (onProgress) onProgress(processed, toProcess.length, item.card_name);
    }
  };
  await Promise.all(Array.from({ length: Math.min(POOL_SIZE, toProcess.length) }, worker));

  const skipped = (items.filter(i => (i.game_type||"Pokemon")==="Pokemon").length) - toProcess.length;
  return {
    results,
    updated:  results.length,
    skipped,
    noPrice:  toProcess.length - results.length,
    total:    toProcess.length,
  };
}
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────



// ── BUYERS LIST CRM ───────────────────────────────────────────────────────────

function toAppBuyer(row) {
  return {
    ID:              row.id,
    VendorID:        row.vendor_id,
    FullName:        row.full_name || "",
    Phone:           row.phone    || "",
    Email:           row.email    || "",
    Notes:           row.notes    || "",
    Address:         row.address  || "",
    City:            row.city     || "",
    State:           row.state    || "",
    Zip:             row.zip      || "",
    StoreCreditBalance: row.store_credit_balance || 0,
    Source:          row.source   || "manual",
    KioskRequestID:  row.kiosk_request_id || null,
    CreatedAt:       row.created_at,
    UpdatedAt:       row.updated_at,
    WantList:        [],
    Purchases:       [],
  };
}

function toAppWantItem(row) {
  return {
    ID:              row.id,
    BuyerID:         row.buyer_id,
    VendorID:        row.vendor_id,
    CardName:        row.card_name   || "",
    SetName:         row.set_name    || "",
    CardNumber:      row.card_number || "",
    MaxPrice:        row.max_price   ? parseFloat(row.max_price) : null,
    Notes:           row.notes       || "",
    Status:          row.status      || "hunting",
    KioskRequestID:  row.kiosk_request_id || null,
    ShowID:          row.show_id     || null,
    ShowName:        row.show_name   || "",
    CreatedAt:       row.created_at,
  };
}

function toAppPurchase(row) {
  return {
    ID:          row.id,
    BuyerID:     row.buyer_id,
    VendorID:    row.vendor_id,
    InventoryID: row.inventory_id,
    SoldPrice:   row.sold_price ? parseFloat(row.sold_price) : null,
    ShowID:      row.show_id    || null,
    Notes:       row.notes      || "",
    PurchasedAt: row.purchased_at,
    CardName:    row.inventory?.card_name  || "",
    SetName:     row.inventory?.set_name   || "",
    ImageURL:    row.inventory?.image_url  || "",
  };
}

// ── READ ─────────────────────────────────────────────────────────────────────

export async function readBuyers() {
  const vendorId = await getVendorId();
  const { data, error } = await supabase
    .from("buyers")
    .select("*")
    .eq("vendor_id", vendorId)
    .order("full_name");
  if (error) throw error;
  return (data || []).map(toAppBuyer);
}

export async function readAllWantListItems(vendorId) {
  const { data, error } = await supabase
    .from("buyer_want_list")
    .select("*, buyers(full_name, phone, email)")
    .eq("vendor_id", vendorId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data || []).map(row => ({
    ...toAppWantItem(row),
    BuyerName:  row.buyers?.full_name || "Unknown",
    BuyerPhone: row.buyers?.phone || "",
    BuyerEmail: row.buyers?.email || "",
    ShowID:     row.show_id   || null,
    ShowName:   row.show_name || "",
  }));
}

export async function readBuyerWantList(buyerId) {
  const { data, error } = await supabase
    .from("buyer_want_list")
    .select("*")
    .eq("buyer_id", buyerId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data || []).map(toAppWantItem);
}

export async function readBuyerPurchases(buyerId) {
  const { data, error } = await supabase
    .from("buyer_purchases")
    .select("*, inventory(card_name, set_name, image_url)")
    .eq("buyer_id", buyerId)
    .order("purchased_at", { ascending: false });
  if (error) throw error;
  return (data || []).map(toAppPurchase);
}

export async function readAllBuyerPurchases(vendorId) {
  const { data, error } = await supabase
    .from("buyer_purchases")
    .select("*")
    .eq("vendor_id", vendorId)
    .order("purchased_at", { ascending: false });
  if (error) throw error;
  return (data || []).map(toAppPurchase);
}

// ── Kiosk Orders (Store Requests) ─────────────────────────────────────────

export async function readKioskOrders(vendorId) {
  const { data, error } = await supabase
    .from("kiosk_orders")
    .select("*, kiosk_order_items(card_name)")
    .eq("vendor_id", vendorId)
    .in("status", ["pending", "pulling", "ready"])
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data || []).map(o => ({
    ID: o.id,
    OrderNumber: o.order_number,
    FirstName: o.first_name,
    LastName: o.last_name,
    Email: o.email || "",
    Phone: o.phone || "",
    Status: o.status,
    CreatedAt: o.created_at,
    CardNames: (o.kiosk_order_items || []).map(i => i.card_name),
  }));
}

export async function readKioskOrderDetail(orderId) {
  const { data: order, error: orderErr } = await supabase
    .from("kiosk_orders").select("*").eq("id", orderId).single();
  if (orderErr) throw orderErr;

  const { data: items, error: itemsErr } = await supabase
    .from("kiosk_order_items")
    .select("*, inventory(status, condition, list_price)")
    .eq("kiosk_order_id", orderId);
  if (itemsErr) throw itemsErr;

  return {
    ID: order.id,
    OrderNumber: order.order_number,
    FirstName: order.first_name,
    LastName: order.last_name,
    Email: order.email || "",
    Phone: order.phone || "",
    Status: order.status,
    CreatedAt: order.created_at,
    Items: (items || []).map(i => ({
      ID: i.id,
      InventoryID: i.inventory_id,
      CardName: i.card_name,
      ListPrice: i.list_price,
      ImageURL: i.image_url,
      Fulfilled: i.fulfilled,
      LiveStatus: i.inventory?.status || null,
      LiveCondition: i.inventory?.condition || null,
      LivePrice: i.inventory?.list_price ?? i.list_price,
    })),
  };
}

export async function updateKioskOrderContact(orderId, { email, phone }) {
  const { error } = await supabase
    .from("kiosk_orders")
    .update({ email: email || null, phone: phone || null })
    .eq("id", orderId);
  if (error) throw error;
}

export async function setKioskOrderItemFulfilled(orderItemId, inventoryId, fulfilled) {
  const { error: itemErr } = await supabase
    .from("kiosk_order_items")
    .update({ fulfilled })
    .eq("id", orderItemId);
  if (itemErr) throw itemErr;

  if (inventoryId) {
    const { error: invErr } = await supabase
      .from("inventory")
      .update(fulfilled
        ? { status: "Reserved", reserved_for: "store_pickup" }
        : { status: "In Stock", reserved_for: null })
      .eq("id", inventoryId);
    if (invErr) throw invErr;
  }
}

export async function removeKioskOrderItem(orderItemId, inventoryId, wasFulfilled) {
  if (wasFulfilled && inventoryId) {
    await supabase.from("inventory")
      .update({ status: "In Stock", reserved_for: null })
      .eq("id", inventoryId);
  }
  const { error } = await supabase.from("kiosk_order_items").delete().eq("id", orderItemId);
  if (error) throw error;
}

export async function completeKioskOrder(order, items, vendor) {
  const today = new Date().toISOString().split("T")[0];
  for (const item of items) {
    if (!item.InventoryID) continue;
    const { TaxRate, TaxAmount } = computeTax(item.LivePrice, vendor);
    await supabase.from("inventory").update({
      status: "Sold",
      sold_price: item.LivePrice,
      sold_date: today,
      sold_channel: "Store-Kiosk",
      reserved_for: null,
      tax_rate: TaxRate,
      tax_amount: TaxAmount,
    }).eq("id", item.InventoryID);
    await removeDealBoardListingIfListed(item.InventoryID);
  }
  const { error } = await supabase
    .from("kiosk_orders")
    .update({ status: "completed", completed_at: new Date().toISOString() })
    .eq("id", order.ID);
  if (error) throw error;

  let buyerAdded = true;
  let buyerError = null;
  try {
    await addBuyer({
      fullName: `${order.FirstName} ${order.LastName}`.trim(),
      email: order.Email || null,
      phone: order.Phone || null,
      source: "store_kiosk",
    });
  } catch (e) {
    buyerAdded = false;
    buyerError = e.message;
    console.warn("Could not auto-add buyer:", e.message);
  }
  return { buyerAdded, buyerError };
}

export async function cancelKioskOrder(order, items) {
  for (const item of items) {
    if (item.Fulfilled && item.InventoryID) {
      await supabase.from("inventory")
        .update({ status: "In Stock", reserved_for: null })
        .eq("id", item.InventoryID);
    }
  }
  const { error } = await supabase
    .from("kiosk_orders")
    .update({ status: "cancelled" })
    .eq("id", order.ID);
  if (error) throw error;
}

// ── CREATE ───────────────────────────────────────────────────────────────────

export async function addBuyer({ fullName, phone, email, notes, source = "manual", kioskRequestId = null }) {
  const vendorId = await getVendorId();
  const { data, error } = await supabase
    .from("buyers")
    .insert({
      vendor_id:        vendorId,
      full_name:        fullName?.trim() || "Unknown",
      phone:            phone?.trim()    || null,
      email:            email?.trim()    || null,
      notes:            notes?.trim()    || null,
      source,
      kiosk_request_id: kioskRequestId  || null,
    })
    .select()
    .single();
  if (error) throw error;
  return toAppBuyer(data);
}

export async function addWantItem(buyerId, { cardName, setName, cardNumber, maxPrice, notes }) {
  const vendorId = await getVendorId();
  const { data, error } = await supabase
    .from("buyer_want_list")
    .insert({
      buyer_id:    buyerId,
      vendor_id:   vendorId,
      card_name:   cardName?.trim()   || "",
      set_name:    setName?.trim()    || null,
      card_number: cardNumber?.trim() || null,
      max_price:   maxPrice           ? parseFloat(maxPrice) : null,
      notes:       notes?.trim()      || null,
      status:      "hunting",
    })
    .select()
    .single();
  if (error) throw error;
  return toAppWantItem(data);
}

export async function addBuyerPurchase(buyerId, { inventoryId, soldPrice, showId, notes }) {
  const vendorId = await getVendorId();
  const { data, error } = await supabase
    .from("buyer_purchases")
    .insert({
      buyer_id:     buyerId,
      vendor_id:    vendorId,
      inventory_id: inventoryId,
      sold_price:   soldPrice ? parseFloat(soldPrice) : null,
      show_id:      showId    || null,
      notes:        notes?.trim() || null,
    })
    .select()
    .single();
  if (error) throw error;
  return toAppPurchase(data);
}

export async function adjustBuyerStoreCredit(buyerId, amount, reason) {
  const vendorId = await getVendorId();
  const { error: txErr } = await supabase
    .from("buyer_store_credit_transactions")
    .insert({
      buyer_id:  buyerId,
      vendor_id: vendorId,
      amount,
      reason: reason?.trim() || null,
    });
  if (txErr) throw txErr;

  const { data: buyer, error: readErr } = await supabase
    .from("buyers").select("store_credit_balance").eq("id", buyerId).single();
  if (readErr) throw readErr;

  const newBalance = (buyer.store_credit_balance || 0) + amount;
  const { error: updErr } = await supabase
    .from("buyers").update({ store_credit_balance: newBalance }).eq("id", buyerId);
  if (updErr) throw updErr;
  return newBalance;
}

// ── UPDATE ───────────────────────────────────────────────────────────────────

export async function updateBuyer(id, { fullName, phone, email, notes }) {
  const updates = {};
  if (fullName  !== undefined) updates.full_name = fullName?.trim() || null;
  if (phone     !== undefined) updates.phone     = phone?.trim()    || null;
  if (email     !== undefined) updates.email     = email?.trim()    || null;
  if (notes     !== undefined) updates.notes     = notes?.trim()    || null;
  const { data, error } = await supabase
    .from("buyers").update(updates).eq("id", id).select().single();
  if (error) throw error;
  return toAppBuyer(data);
}

export async function updateWantItem(id, { status, maxPrice, notes }) {
  const updates = {};
  if (status   !== undefined) updates.status    = status;
  if (maxPrice !== undefined) updates.max_price = maxPrice ? parseFloat(maxPrice) : null;
  if (notes    !== undefined) updates.notes     = notes?.trim() || null;
  const { data, error } = await supabase
    .from("buyer_want_list").update(updates).eq("id", id).select().single();
  if (error) throw error;
  return toAppWantItem(data);
}

// ── DELETE ───────────────────────────────────────────────────────────────────

export async function deleteBuyer(id) {
  const { error } = await supabase.from("buyers").delete().eq("id", id);
  if (error) throw error;
}

export async function deleteWantItem(id) {
  const { error } = await supabase.from("buyer_want_list").delete().eq("id", id);
  if (error) throw error;
}

// ── KIOSK PIN & SLUG ──────────────────────────────────────────────────────────
const KIOSK_AUTH_PROXY = "/kiosk-auth";

export async function setKioskPin(vendorId, pin) {
  const res = await fetch(KIOSK_AUTH_PROXY, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "set", vendorId, pin }),
  });
  const data = await res.json();
  if (!data.ok) throw new Error(data.error || "Failed to set PIN");
  return true;
}

export async function getKioskSlug() {
  const vendorId = await getVendorId();
  const { data, error } = await supabase
    .from("vendors")
    .select("kiosk_slug, business_name")
    .eq("id", vendorId)
    .single();
  if (error) throw error;
  return data;
}

// ── DEMO MODE ─────────────────────────────────────────────────────────────────

export const DEMO_VENDOR_ID = "d0000000-0000-0000-0000-000000000001";

export async function loadDemoData() {
  const vid = DEMO_VENDOR_ID;

  const [vendor, inv, lots, shows, showItems, consignors, buyers, wantList, claims] =
    await Promise.all([
      supabase.from("vendors").select("*").eq("id", vid).single()
        .then(r => r.data),
      supabase.from("inventory").select("*").eq("vendor_id", vid)
        .then(r => (r.data || []).map(toApp)),
      supabase.from("lots").select("*").eq("vendor_id", vid)
        .then(r => (r.data || []).map(lotToApp)),
      supabase.from("shows").select("*").eq("vendor_id", vid)
        .then(r => (r.data || []).map(showToApp)),
      supabase.from("show_items").select("*").eq("vendor_id", vid)
        .then(r => (r.data || []).map(r2 => ({ ShowID: r2.show_id, InventoryID: r2.inventory_id }))),
      supabase.from("consignors").select("*").eq("vendor_id", vid)
        .then(r => (r.data || []).map(consignorToApp)),
      supabase.from("buyers").select("*").eq("vendor_id", vid)
        .then(r => (r.data || []).map(toAppBuyer)),
      supabase.from("buyer_want_list").select("*").eq("vendor_id", vid)
        .then(r => r.data || []),
      supabase.from("claims").select("*").eq("vendor_id", vid)
        .order("created_at", { ascending: false })
        .then(r => r.data || []),
    ]);

  return { vendor, inv, lots, shows, showItems, consignors, buyers, wantList, claims };
}

// ── Team Management ───────────────────────────────────────────────────────────

export async function getVendorRole(email, vendorId) {
  const { data } = await supabase
    .from("vendor_users")
    .select("id,role")
    .eq("email", email.toLowerCase())
    .eq("vendor_id", vendorId)
    .single();
  return data ? { id: data.id, role: data.role } : null;
}

export async function readTeamMembers(vendorId) {
  const { data, error } = await supabase
    .from("vendor_users")
    .select("id,email,role,created_at,created_by")
    .eq("vendor_id", vendorId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function linkSeatSubscription(subscriptionId) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch("/link-seat-subscription", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
    },
    body: JSON.stringify({ subscriptionId }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || "Could not link seat subscription");
  return body;
}

export async function linkDealBoardPlusSubscription(subscriptionId, phone) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch("/link-deal-board-plus-subscription", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
    },
    body: JSON.stringify({ subscriptionId, phone }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || "Could not link Deal Board Plus subscription");
  return body;
}

export async function linkDynamicPricingSubscription(subscriptionId) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch("/link-dynamic-pricing-subscription", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
    },
    body: JSON.stringify({ subscriptionId }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || "Could not link Dynamic Pricing subscription");
  return body;
}

// ── Deal Board — contact info ────────────────────────────────────────────────
export async function acceptDealBoardGuidelines(vendorId) {
  const { error } = await supabase
    .from("vendors")
    .update({ deal_board_guidelines_accepted: true })
    .eq("id", vendorId);
  if (error) throw error;
}

export async function saveDealBoardPhone(vendorId, phone) {
  const { error } = await supabase
    .from("vendors")
    .update({ deal_board_phone: phone || null })
    .eq("id", vendorId);
  if (error) throw error;
}

// ── Deal Board — listings ────────────────────────────────────────────────────

export async function listOnDealBoard(item, vendorId) {
  const { error } = await supabase.from("deal_board_listings").insert({
    vendor_id: vendorId,
    inventory_id: item.ID,
    card_name: item.CardName,
    image_url: item.ImageURL || null,
    condition: item.Condition || null,
    game_type: item.GameType || "Pokemon",
    asking_price: parseFloat(item.ListPrice) || 0,
  });
  if (error) throw error;
}

export async function postStandaloneDealBoardListing(vendorId, { cardName, imageUrl, condition, price, gameType, state }) {
  const { error } = await supabase.from("deal_board_listings").insert({
    vendor_id: vendorId,
    inventory_id: null,
    card_name: cardName,
    image_url: imageUrl || null,
    condition: condition || null,
    game_type: gameType,
    asking_price: parseFloat(price) || 0,
    state: state || null,
  });
  if (error) throw error;
}

export async function unlistFromDealBoard(inventoryId, vendorId) {
  const { error } = await supabase
    .from("deal_board_listings")
    .update({ status: "removed" })
    .eq("inventory_id", inventoryId)
    .eq("vendor_id", vendorId);
  if (error) throw error;
}

export async function unlistDealBoardById(listingId, vendorId) {
  const { error } = await supabase
    .from("deal_board_listings")
    .update({ status: "removed" })
    .eq("id", listingId)
    .eq("vendor_id", vendorId);
  if (error) throw error;
}

export async function adminRemoveDealBoardListing(listingId) {
  const { data, error } = await supabase.rpc("admin_remove_deal_board_listing", { p_listing_id: listingId });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
}

export async function removeDealBoardListingIfListed(inventoryId) {
  if (!inventoryId) return;
  try {
    await supabase
      .from("deal_board_listings")
      .update({ status: "removed" })
      .eq("inventory_id", inventoryId)
      .eq("status", "active");
  } catch (e) {
    console.warn("Could not auto-remove Deal Board listing (non-blocking):", e.message);
  }
}

export async function getMyDealBoardListings(vendorId) {
  const { data, error } = await supabase
    .from("deal_board_listings")
    .select("*")
    .eq("vendor_id", vendorId)
    .eq("status", "active")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data || []).map(r => ({
    ID: r.id, InventoryID: r.inventory_id, CardName: r.card_name,
    ImageURL: r.image_url, Condition: r.condition, GameType: r.game_type,
    AskingPrice: parseFloat(r.asking_price) || 0, CreatedAt: r.created_at,
  }));
}

export async function getDealBoardFeed({ limit = 24, offset = 0, search = "", gameType = "all", priceMax = "", listingIds = null, vendorIds = null } = {}) {
  const { data, error } = await supabase.rpc("get_deal_board_feed", {
    p_limit: limit,
    p_offset: offset,
    p_search: search.trim() || null,
    p_game_type: gameType === "all" ? null : gameType,
    p_price_max: priceMax ? parseFloat(priceMax) : null,
    p_listing_ids: listingIds,
    p_vendor_ids: vendorIds,
  });
  if (error) throw error;
  const rows = (data || []).map(r => ({
    ID: r.id, VendorID: r.vendor_id, CardName: r.card_name, ImageURL: r.image_url,
    Condition: r.condition, GameType: r.game_type, AskingPrice: parseFloat(r.asking_price) || 0,
    CreatedAt: r.created_at, VendorName: r.vendor_name || null, IsPro: !!r.is_pro, State: r.state || null,
  }));
  const totalCount = data?.[0]?.total_count != null ? parseInt(data[0].total_count) : 0;
  return { rows, totalCount };
}

// ── Deal Board — favorites & following ───────────────────────────────────────

export async function getMyFavoriteIds(vendorId) {
  const { data, error } = await supabase
    .from("deal_board_favorites").select("listing_id").eq("vendor_id", vendorId);
  if (error) throw error;
  return (data || []).map(r => r.listing_id);
}

export async function toggleFavorite(vendorId, listingId, currentlyFavorited) {
  if (currentlyFavorited) {
    const { error } = await supabase.from("deal_board_favorites").delete()
      .eq("vendor_id", vendorId).eq("listing_id", listingId);
    if (error) throw error;
  } else {
    const { error } = await supabase.from("deal_board_favorites")
      .insert({ vendor_id: vendorId, listing_id: listingId });
    if (error) throw error;
  }
}

export async function getMyFollowedVendorIds(vendorId) {
  const { data, error } = await supabase
    .from("deal_board_follows").select("followed_vendor_id").eq("follower_vendor_id", vendorId);
  if (error) throw error;
  return (data || []).map(r => r.followed_vendor_id);
}

export async function toggleFollow(vendorId, followedVendorId, currentlyFollowing) {
  if (currentlyFollowing) {
    const { error } = await supabase.from("deal_board_follows").delete()
      .eq("follower_vendor_id", vendorId).eq("followed_vendor_id", followedVendorId);
    if (error) throw error;
  } else {
    const { error } = await supabase.from("deal_board_follows")
      .insert({ follower_vendor_id: vendorId, followed_vendor_id: followedVendorId });
    if (error) throw error;
  }
}

// ── Deal Board — messaging ───────────────────────────────────────────────────

export async function sendDealBoardMessage(listingId, recipientId, body, requestId = null) {
  const { data, error } = await supabase.rpc("send_deal_board_message", {
    p_listing_id: listingId, p_recipient_id: recipientId, p_body: body, p_request_id: requestId,
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}

// ── Deal Board — Wanted Items (requests) ─────────────────────────────────────

// ── Deal Board — post cap (Free / Deal Board Plus / Pro) ────────────────────
export function getDealBoardPostCap(vendor) {
  if (vendor?.role === "admin" || ["monthly","annual","trial"].includes(vendor?.plan)) return Infinity;
  if (vendor?.plan === "deal_board_plus") return 30;
  return 10;
}

export async function getDealBoardPostCountThisMonth(vendorId) {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

  const [{ count: listingCount }, { count: requestCount }] = await Promise.all([
    supabase.from("deal_board_listings").select("id", { count: "exact", head: true })
      .eq("vendor_id", vendorId).gte("created_at", monthStart),
    supabase.from("deal_board_requests").select("id", { count: "exact", head: true })
      .eq("vendor_id", vendorId).gte("created_at", monthStart),
  ]);
  return (listingCount || 0) + (requestCount || 0);
}

export async function postDealBoardRequest(vendorId, { cardName, condition, price }) {
  const { error } = await supabase.from("deal_board_requests").insert({
    vendor_id: vendorId, card_name: cardName, condition, price: parseFloat(price) || 0,
  });
  if (error) throw error;
}

export async function getDealBoardRequestsFeed({ limit = 24, offset = 0, search = "" } = {}) {
  const { data, error } = await supabase.rpc("get_deal_board_requests_feed", {
    p_limit: limit,
    p_offset: offset,
    p_search: search.trim() || null,
  });
  if (error) throw error;
  const rows = (data || []).map(r => ({
    ID: r.id, VendorID: r.vendor_id, VendorName: r.vendor_name || "Vendor",
    IsPro: !!r.is_pro, CardName: r.card_name, Condition: r.condition,
    Price: parseFloat(r.price) || 0, CreatedAt: r.created_at, State: r.state || null,
  }));
  const totalCount = data?.[0]?.total_count != null ? parseInt(data[0].total_count) : 0;
  return { rows, totalCount };
}

export async function getMyDealBoardRequests(vendorId) {
  const { data, error } = await supabase
    .from("deal_board_requests")
    .select("*")
    .eq("vendor_id", vendorId)
    .in("status", ["active", "fulfilled", "cancelled"])
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data || []).map(r => ({
    ID: r.id, CardName: r.card_name, Condition: r.condition,
    Price: parseFloat(r.price) || 0, Status: r.status, CreatedAt: r.created_at,
  }));
}

export async function updateDealBoardRequestStatus(requestId, status) {
  const { error } = await supabase.from("deal_board_requests")
    .update({ status }).eq("id", requestId);
  if (error) throw error;
}

export async function getDealBoardConnections(vendorId) {
  const { data: conns, error } = await supabase
    .from("deal_board_connections")
    .select("*")
    .or(`vendor_a_id.eq.${vendorId},vendor_b_id.eq.${vendorId}`)
    .order("created_at", { ascending: false });
  if (error) throw error;

  const otherIds = (conns || []).map(c => c.vendor_a_id === vendorId ? c.vendor_b_id : c.vendor_a_id);
  const { data: others } = otherIds.length
    ? await supabase.from("vendors").select("id, business_name").in("id", otherIds)
    : { data: [] };
  const nameById = Object.fromEntries((others || []).map(v => [v.id, v.business_name]));

  const enriched = await Promise.all((conns || []).map(async c => {
    const otherId = c.vendor_a_id === vendorId ? c.vendor_b_id : c.vendor_a_id;
    const { data: lastMsg } = await supabase
      .from("deal_board_messages")
      .select("body, created_at, sender_vendor_id")
      .or(`sender_vendor_id.eq.${otherId},recipient_vendor_id.eq.${otherId}`)
      .order("created_at", { ascending: false }).limit(1).maybeSingle();
    return {
      ID: c.id, OtherVendorID: otherId, OtherVendorName: nameById[otherId] || "Vendor",
      Status: c.status, RequestedBy: c.requested_by, IsIncoming: c.requested_by !== vendorId,
      LastMessage: lastMsg?.body || null, LastMessageAt: lastMsg?.created_at || c.created_at,
    };
  }));
  return enriched.sort((a,b) => new Date(b.LastMessageAt) - new Date(a.LastMessageAt));
}

export async function getDealBoardThread(vendorId, otherVendorId) {
  const { data, error } = await supabase
    .from("deal_board_messages")
    .select("*, deal_board_listings(card_name)")
    .or(`and(sender_vendor_id.eq.${vendorId},recipient_vendor_id.eq.${otherVendorId}),and(sender_vendor_id.eq.${otherVendorId},recipient_vendor_id.eq.${vendorId})`)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data || []).map(m => ({
    ID: m.id, SenderID: m.sender_vendor_id, Body: m.body, CreatedAt: m.created_at,
    CardName: m.deal_board_listings?.card_name || null, Read: m.read,
  }));
}

export async function deleteDealBoardMessage(messageId) {
  const { data, error } = await supabase.rpc("admin_delete_deal_board_message", { p_message_id: messageId });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
}

export async function markDealBoardThreadRead(vendorId, otherVendorId) {
  await supabase.from("deal_board_messages")
    .update({ read: true })
    .eq("recipient_vendor_id", vendorId).eq("sender_vendor_id", otherVendorId).eq("read", false);
}

export async function getDealBoardUnreadCount(vendorId) {
  const { count } = await supabase
    .from("deal_board_messages")
    .select("id", { count: "exact", head: true })
    .eq("recipient_vendor_id", vendorId).eq("read", false);
  return count || 0;
}

export async function respondToDealBoardConnection(vendorId, otherVendorId, accept) {
  const vlow = vendorId < otherVendorId ? vendorId : otherVendorId;
  const vhigh = vendorId < otherVendorId ? otherVendorId : vendorId;
  const { error } = await supabase.from("deal_board_connections")
    .update({ status: accept ? "accepted" : "declined", responded_at: new Date().toISOString() })
    .eq("vendor_a_id", vlow).eq("vendor_b_id", vhigh);
  if (error) throw error;
}

export async function blockDealBoardVendor(vendorId, blockedVendorId) {
  const { error } = await supabase.from("deal_board_blocks")
    .insert({ blocking_vendor_id: vendorId, blocked_vendor_id: blockedVendorId });
  if (error) throw error;
}


export async function addTeamMember(vendorId, email, role, createdBy) {
  const normalized = email.trim().toLowerCase();
  const { data: existing } = await supabase
    .from("vendor_users")
    .select("vendor_id")
    .eq("email", normalized)
    .single();
  if (existing) {
    throw new Error("This email is already associated with another VendorStax account.");
  }
  const { data, error } = await supabase
    .from("vendor_users")
    .insert({ vendor_id: vendorId, email: normalized, role, created_by: createdBy })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function removeTeamMember(id) {
  const { error } = await supabase
    .from("vendor_users")
    .delete()
    .eq("id", id);
  if (error) throw error;
}

export async function updateTeamMemberRole(id, role) {
  const { data, error } = await supabase
    .from("vendor_users")
    .update({ role })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Audit Log ─────────────────────────────────────────────────────────────────

export async function writeAuditLog(vendorId, userEmail, action, recordId, recordName, oldValue, newValue) {
  supabase.from("audit_log").insert({
    vendor_id:   vendorId,
    user_email:  userEmail,
    action,
    record_id:   recordId || null,
    record_name: recordName || null,
    old_value:   oldValue  || null,
    new_value:   newValue  || null,
  }).then(() => {}).catch(() => {});
}

// ── Trade Photos ─────────────────────────────────────────────────────────────

export async function addTradePhoto(vendorId, showId, photoData, cardsOut, cardsIn) {
  const { data, error } = await supabase
    .from("trade_photos")
    .insert({
      vendor_id:  vendorId,
      show_id:    showId,
      photo_data: photoData,
      cards_out:  cardsOut || null,
      cards_in:   cardsIn  || null,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function readTradePhotos(showId) {
  const { data, error } = await supabase
    .from("trade_photos")
    .select("*")
    .eq("show_id", showId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function linkPayPalSubscription(subscriptionId, phone) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch("/paypal-link-subscription", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
    },
    body: JSON.stringify({ subscriptionId, phone }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || "Failed to link subscription");
  return body;
}

export async function checkTrialStatus(action = null) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) return null;
  const res = await fetch("/check-trial", {
    method: "POST",
    headers: { Authorization: `Bearer ${session.access_token}`, "Content-Type": "application/json" },
    body: JSON.stringify(action ? { action } : {}),
  });
  if (!res.ok) return null;
  return res.json();
}

export async function startProTrial(vendorId, showId, showEndDate) {
  const end = new Date(showEndDate + "T00:00:00Z");
  end.setUTCDate(end.getUTCDate() + 5);
  const { error } = await supabase
    .from("vendors")
    .update({
      trial_used: true,
      plan: "trial",
      trial_ends_at: end.toISOString(),
      trial_show_id: showId,
    })
    .eq("id", vendorId);
  if (error) throw error;
}

export async function declineProTrial(vendorId) {
  const { error } = await supabase
    .from("vendors")
    .update({ trial_used: true })
    .eq("id", vendorId);
  if (error) throw error;
}

export async function notifyBuyerCardFound({ buyerEmail, buyerName, cardName, businessName }) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch("/notify-buyer", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
    },
    body: JSON.stringify({ buyerEmail, buyerName, cardName, businessName }),
  });
  const result = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(result.error || "Failed to send email");
  return result;
}

export async function submitSupportTicket(ticket) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch("/support-ticket", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
    },
    body: JSON.stringify(ticket),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || "Failed to submit ticket");
  return body;
}

export async function readAuditLog(vendorId, limit = 200) {
  const cutoff = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();
  supabase.from("audit_log").delete()
    .eq("vendor_id", vendorId)
    .lt("created_at", cutoff)
    .then(() => {}).catch(() => {});

  const { data, error } = await supabase
    .from("audit_log")
    .select("*")
    .eq("vendor_id", vendorId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

// ── Shop Claims (CRM) ─────────────────────────────────────────────────────────

export async function readClaims(vendorId, limit = 200) {
  const { data, error } = await supabase
    .from("claims")
    .select("*")
    .eq("vendor_id", vendorId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

export async function addClaim(vendorId, userEmail, {
  inventoryId, cardName, listPrice, offerPrice, customerName, customerContact, notes,
}) {
  const { data, error } = await supabase
    .from("claims")
    .insert({
      vendor_id:        vendorId,
      inventory_id:     inventoryId || null,
      card_name:        cardName?.trim(),
      list_price:       listPrice  != null && listPrice  !== "" ? parseFloat(listPrice)  : null,
      offer_price:      offerPrice != null && offerPrice !== "" ? parseFloat(offerPrice) : null,
      customer_name:    customerName?.trim(),
      customer_contact: customerContact?.trim(),
      notes:            notes?.trim() || null,
      status:           "pending",
    })
    .select()
    .single();
  if (error) throw error;
  writeAuditLog(vendorId, userEmail, "claim.created", data.id, data.card_name, null,
    `Manually added · ${data.customer_name}`);
  return data;
}

export async function updateClaim(id, vendorId, userEmail, updates, prevClaim) {
  const patch = {};
  if (updates.cardName        !== undefined) patch.card_name        = updates.cardName?.trim();
  if (updates.customerName    !== undefined) patch.customer_name    = updates.customerName?.trim();
  if (updates.customerContact !== undefined) patch.customer_contact = updates.customerContact?.trim();
  if (updates.listPrice       !== undefined) patch.list_price       = updates.listPrice  !== "" ? parseFloat(updates.listPrice)  : null;
  if (updates.offerPrice      !== undefined) patch.offer_price      = updates.offerPrice !== "" ? parseFloat(updates.offerPrice) : null;
  if (updates.notes           !== undefined) patch.notes            = updates.notes?.trim() || null;
  if (updates.status          !== undefined) patch.status           = updates.status;

  const { data, error } = await supabase
    .from("claims")
    .update(patch)
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;

  if (updates.status !== undefined && prevClaim && updates.status !== prevClaim.status) {
    writeAuditLog(vendorId, userEmail, "claim.status_changed", id, data.card_name,
      prevClaim.status, updates.status);
  } else {
    writeAuditLog(vendorId, userEmail, "claim.updated", id, data.card_name, null, null);
  }
  return data;
}

export async function deleteClaim(id, vendorId, userEmail, claim) {
  const { error } = await supabase.from("claims").delete().eq("id", id);
  if (error) throw error;
  writeAuditLog(vendorId, userEmail, "claim.deleted", id, claim?.card_name || null,
    claim?.status || null, null);
}

export async function readClaimHistory(vendorId, claimId) {
  const { data, error } = await supabase
    .from("audit_log")
    .select("*")
    .eq("vendor_id", vendorId)
    .eq("record_id", claimId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function acceptClaimOffer(claimId, vendorId) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch("/accept-offer", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
    },
    body: JSON.stringify({ claimId, vendorId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Failed to accept offer");
  return data;
}
